import throttle from 'lodash/throttle';
import { glow, patchHTML, recentInteraction } from './liveView';
import { toCents, toDollars, priceTotals } from './money';
import { usesDockmaster } from './marinaConfig';
import { marinaLatLng } from './marinaLocation';
import { initTidePanel } from './tidePanel';
import { DB_URL, firebaseConfig } from './firebaseConfig';
import debounce from 'lodash/debounce';

import { app, auth, database, functions, storage, isFirebaseConnected, waitForFirebaseReady, onFirebaseConnectedChange } from './initFirebase';
import { rememberBrand, cachedBrand } from './brandCache';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { createSignIn } from './signIn';
import {
  ref, get, onValue, query, orderByChild, startAt,
  child, onChildAdded, onChildChanged, onChildRemoved,
  equalTo, runTransaction
} from 'firebase/database';
import { set, update, remove, pendingWrites, onPendingWritesChange } from './trackedDb'; // durable offline writes
import { pruneDeletedJobs } from './jobCache';
import { catchUp, watch as watchChanges } from './changeFeed';
import { deviceCopy } from './deviceCopy';
import { rememberTechs, techLabel, techsLabel } from './techNames';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject
} from 'firebase/storage';

import { formatDateToMMDDYYYY, formatDateToYYYYMMDD, parseDay } from './date-format';
import {
  aggregateWorkOrder, resolveAttentionSlot, techAvatar, splitTechs,
  formatShortDate, outstandingPartsForJob, resolveOperationSubline,
  operationSeverity, compareUrgency, urgencyRank, isThisWeek, earliestOverdueEta,
} from './wo-ops-aggregation';
import { renderOpPanel, refreshOpPanelLive, openDeclineConfirmDialog, checkFollowUpDue } from './opPanel';
import { opRowChecklistHtml } from './checklistOffice';
import {
  setEstimateDeps, isEstimateMode, resetEstimateState, renderEstimateOperations,
  addEstimateOperation, saveEstimate, fmtEstDocDate, openInvoiceDocument, openEstimateById, openEstimatesList, syncEstimatesToDevice,
} from './estimates';
import { billedJobsByOpCode, billingStats } from './billing-history';
import { mountScheduleAssist, refreshProjectionSoon, onProjectionChange, workOrderProjection, projectionLabel } from './scheduleAssist';
import {
  getDB, idbGet, idbPut, idbCursorAll,
  generateId, toastMessage, fetchFirebaseLastUpdated, fetchIndexedDBLastUpdated,
  updateTimestamps, fetchIdToken, getCustomerDMID,
  getCustomerBalance, getUserRole, fetchOperationsForWorkOrder,
  fetchDMWorkOrder, updateDMWorkOrder, deleteDMOperationProxy,
  setReadOnlyMode, isReadOnlyMode, blockIfReadOnly,
  isAdminRole, isValidRole, escapeHTML,
  nextEstimateNumber, saveEstimateRecord,
  logPesticideApplication, adjustInventoryQuantity,
  nextPurchaseOrderNumber, savePurchaseOrderRecord,
  STORAGE_SERVICE_CATALOG, STORAGE_DEFAULTS, currentSeasonLabel,
  woLabel, nextInternalWorkOrderNumber, notifyLocalDataChanged, isShrinkwrapInstall } from './utilities';
import { lineDue } from './poReceivingCore';
import { setBillingDeps, billingModal } from './billing';
import { setStorageContractDeps, renderStorageContractsList, suppressWorkOrderAddedToast, setContractsUnsignedOnly } from './storageContracts';
import { setShrinkwrapViewDeps, renderShrinkwrapView, setShrinkwrapStage } from './shrinkwrapView';
import { renderCanvasView, setCanvasFilter } from './canvasView';
import { setInternalViewDeps, renderInternalView } from './internalView';
import { offerCanvasMove } from './canvasOpOffer';
import { setDailyLogDeps, formatYMDLocal, loadDailyLog } from './dailyLog';
import { initShellNav, closeUserMenu } from './shellNav';
import { initShellKeys } from './shellKeys';
import { initMobileBar } from './mobileBar';
import { openLauncher } from './railStyles';
import { HUBS, initRailHubs, selectHubTab } from './railHubs';
import { initRailLayout, refreshRail } from './railLayout';
import { initInvoices, invoiceWorkOrder, openInvoicesList, showStatement, openInvoiceEditor } from './invoices';
import { initEvents } from './events';
import { watchFeatures } from './marinaFeatures';
import { watchNoMatch, emptyHTML, errorHTML, errorCode } from './uiStates';
import { openCompose } from './mailClient';
import { showUpdatePrompt } from './updatePrompt';
import { updatePhotoBadge } from './jobPhotos';
import { setPartsKitDeps, addPartToPartsKit } from './partsKits';
import { setWoListActionDeps } from './woListActions';
import { setDeviceSyncDeps, getJobsOpen, getCustomerOpen, getOpCodes, getMapData, getInventory, getInventoryPOs, getReceiving, getVendors, getMooringDatabaseOpen, getRentalDatabaseOpen, getJobsClosed } from './deviceSync';
import { checkForPesticideUsage } from './pesticideLog';
import { setOpCodePickerDeps, setCurrentOpCodeData, updateOpenDataDB, processOpCodes, codeSearch, renderWoValidationChecklist } from './opCodePicker';
import { clearPickerSearch, searchGeneralInventory } from './partSearch';
import { setQuickSelectDeps, renderPartsData, renderLaborData } from './quickSelect';
import { setNotesPanelDeps, openNotesPanel, addJobNote, updateJobNotes } from './notesPanel';
import { setWoFilterDeps, Sort, applyFilters, billingJobsActive, closedJobsActive, billingWorkOrdersActive, closedWorkOrdersActive, applySorting, updateTable, clearFilters, updateJobQuickFilterCounts, getJobDataIndex } from './woFilters';
import { setTechWeekDeps, globalTechStats, initializeWeekStart, updateWeekView, populateTechSelectModal, load } from './techWeek';
import { setLaborPartsDeps, fetchParts, addLaborFirebase, getTodaysDate, addParts, showDescriptionPopup, createPopup, closePopup, reduceInventory, addPartsFirebase, getJobData, updateLaborIndex, laborSummaryFromDOM, showStatus, addPartsKitToJob, refreshOpenLaborParts, generateLaborTable, isMiscPartNumber } from './laborParts';
import { setNewCustomerDeps, addNewCustomer, showWeekCounts } from './newCustomer';
import { setWoTableDeps, createOperationRow, setUpWorkOrderStatuses, renderTable, woStatusToneSlug, refreshWorkOrderRow, getOpenWorkOrdersForBoat, createNewTableRow, statusToneSlug, OP_ROW_PILL_TONE, OP_ROW_PILL_LABEL, refreshOpRowSeverity, syncOpRowBillButton, opProgressHeaderHtml, opSummaryChipsHtml, syncOpenOperationsPanel, opTableList, createNewOpRow } from './woTable';
import { setAppMode } from './appMode';
import { loadSecondaryIframesLazy, showPage, checkAuthState, getCurrentUserData, logoutUser, waitForAuthUser } from './shellPages';
import { setShellAuthDeps, handleAuthStateChange, otherPageOnScreen, bootSwitchView } from './shellAuth';
import { setShellBootDeps, collectTechInformation, onlineFlow, offlineFlow } from './shellBoot';
import { setDataListenerDeps, glowJobRows, initializeDataListener } from './dataListener';
import { setWoFormDeps, setPendingOpSchedule, resetWoEditorsExpanded, syncWoContextStrip, selectCustomerAndBoat, openWoCustomerPicker, jobAssist, closeAdd, AddRow, STORAGE_PRICING_FALLBACK, storageContractSettings, storageTierFor, storagePrice, addJob, updateWoOpsSummary, deleteJob, editingOperationJobBox, saveEditedOperation, getWorkOrderNumber, getWorkOrderNumberMove, populateTable, wakeShrinkwrapCard, addHaulLaunchStorageDates } from './woForm';
import { setAddOperationDeps, addOperation, openAddOperationModal } from './addOperation';
import { setWoSearchDeps, searchCall, searchJobs, searchClosedJobsIndexedDB, closedJobList } from './woSearch';
import { setJobWritesDeps, getCustomerRecord, updateJobDB, getJobDataDB, getJobsByWorkOrder, getTechId, addJobFirebase, updateJobStatus, updateJobInfo, updateCustomerInfo, updateBoatInfo } from './jobWrites';
import { setOpStatusDeps, openJobCompleteConfirm, submitOpen, changeStatus, updateOperationTableUI } from './opStatus';
import { setOpActionsDeps, addOpen, buildOpContextText, opCheck, refreshOpenOpPanel, editWorkOrderData, shrinkwrapLabor, deleteOperation, deleteOperationOtherUser } from './opActions';
import { setWoExpandDeps, updateWorkOrderDM, deleteOperationDM, selectWorkOrder, removeOperationDetails, displayOperations, lookupDockmasterInvoiceForWorkOrder, openWoInvoicePreview } from './woExpand';
import './woExports';

// Live bindings opPanel.js needs from this module (circular import — safe
// because opPanel.js only reads these from inside its own functions, never
// at its own module-evaluation time). See the plan's architectural linchpin
// note for why opPanel.js isn't a second <script> tag: this whole app is a
// single webpack entry (openWork), so cross-file sharing goes through ES
// module import/export, not window globals.
export { jobsByWorkOrder, opCheck, statusToneSlug, deleteOperationDM, updateWorkOrderDM, refreshWorkOrderRow, refreshOpRowSeverity, updateJobInfo, updateCustomerInfo, getCurrentUserData, formatDate, getOpenWorkOrdersForBoat, getCustomerRecord, getJobData, getFormattedDate, updateJobNotes, addJobNote, editWorkOrderData, globalUserData, addOperation, shrinkwrapLabor };
// currentURL is declared just below with `const` — exported via this
// separate statement (not `export const currentURL = ...`) to keep the
// declaration line itself untouched.
export { currentURL };

// woExpand.js reaches these through this object instead of importing this file back (it sets
// activeRow, activeTableRow and globalWorkOrder, which stay here).
setWoExpandDeps({
  get activeRow() { return activeRow; },
  set activeRow(v) { activeRow = v; },
  get activeTableRow() { return activeTableRow; },
  set activeTableRow(v) { activeTableRow = v; },
  get globalWorkOrder() { return globalWorkOrder; },
  set globalWorkOrder(v) { globalWorkOrder = v; },
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  get userRole() { return userRole; },
  getCustomerDataById: (...a) => getCustomerDataById(...a),
  callOpenCustomerData: (...a) => callOpenCustomerData(...a),
});

// opActions.js reaches these through this object instead of importing this file back (it sets
// selectedJobRow, globalJobId and globalShrink, which stay here). The opPanel.js functions come
// through here too: opPanel.js imports this file, so opActions.js importing it would add a cycle.
setOpActionsDeps({
  get selectedJobRow() { return selectedJobRow; },
  set selectedJobRow(v) { selectedJobRow = v; },
  get globalJobId() { return globalJobId; },
  set globalJobId(v) { globalJobId = v; },
  get globalShrink() { return globalShrink; },
  set globalShrink(v) { globalShrink = v; },
  get globalUserData() { return globalUserData; },
  get activeTableRow() { return activeTableRow; },
  get currentURL() { return currentURL; },
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  formatPhoneNumber: (...a) => formatPhoneNumber(...a),
  getCustomerDataById: (...a) => getCustomerDataById(...a),
  syncUpdateStatusTone: (...a) => syncUpdateStatusTone(...a),
  deleteOperationDM: (...a) => deleteOperationDM(...a),
  updateWorkOrderDM: (...a) => updateWorkOrderDM(...a),
  formatDate: (...a) => formatDate(...a),
  removeOperationDetails: (...a) => removeOperationDetails(...a),
  renderOpPanel: (...a) => renderOpPanel(...a),
  refreshOpPanelLive: (...a) => refreshOpPanelLive(...a),
  openDeclineConfirmDialog: (...a) => openDeclineConfirmDialog(...a),
});

// opStatus.js reaches these through this object instead of importing this file back.
setOpStatusDeps({
  get completeRed() { return completeRed; },
  get progressYellow() { return progressYellow; },
  checkOverallStatus: (...a) => checkOverallStatus(...a),
});

// jobWrites.js reaches these through this object instead of importing this file back.
setJobWritesDeps({
  get currentURL() { return currentURL; },
  opCodeCheck: (...a) => opCodeCheck(...a),
});

// woSearch.js reaches these through this object instead of importing this file back (the Closed
// list sets latestReceivingData and latestCustomerDataById, which stay here).
setWoSearchDeps({
  get latestReceivingData() { return latestReceivingData; },
  set latestReceivingData(v) { latestReceivingData = v; },
  get latestCustomerDataById() { return latestCustomerDataById; },
  set latestCustomerDataById(v) { latestCustomerDataById = v; },
  renderJobsInChunks: (...a) => renderJobsInChunks(...a),
  loadOutstandingJobPartOrders: (...a) => loadOutstandingJobPartOrders(...a),
  fetchDataFromStore: (...a) => fetchDataFromStore(...a),
  lazyLoadJobs: (...a) => lazyLoadJobs(...a),
  opCheck: (...a) => opCheck(...a),
});

// addOperation.js reaches these through this object instead of importing this file back (it sets
// activeRow, which stays here).
setAddOperationDeps({
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  get latestReceivingData() { return latestReceivingData; },
  get activeRow() { return activeRow; },
  set activeRow(v) { activeRow = v; },
  get globalWorkOrder() { return globalWorkOrder; },
  getCustomerRecord: (...a) => getCustomerRecord(...a),
  addOpen: (...a) => addOpen(...a),
  removeOperationDetails: (...a) => removeOperationDetails(...a),
  displayOperations: (...a) => displayOperations(...a),
});

// woForm.js reaches these through this object instead of importing this file back.
setWoFormDeps({
  get clerkIdInfo() { return clerkIdInfo; },
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  get currentOpCodes() { return currentOpCodes; },
  get currentURL() { return currentURL; },
  formatPhoneNumber: (...a) => formatPhoneNumber(...a),
  createNew: (...a) => createNew(...a),
  getCustomerRecord: (...a) => getCustomerRecord(...a),
  addJobFirebase: (...a) => addJobFirebase(...a),
  updateWorkOrderDM: (...a) => updateWorkOrderDM(...a),
  formatDate: (...a) => formatDate(...a),
  deleteOperationDM: (...a) => deleteOperationDM(...a),
});

// dataListener.js reaches these through this object instead of importing this file back.
setDataListenerDeps({
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  refreshOpenOpPanel: (...a) => refreshOpenOpPanel(...a),
  submitOpen: (...a) => submitOpen(...a),
  changeStatus: (...a) => changeStatus(...a),
  updateOperationTableUI: (...a) => updateOperationTableUI(...a),
  deleteOperationOtherUser: (...a) => deleteOperationOtherUser(...a),
  updateJobRow: (...a) => updateJobRow(...a),
});

// shellBoot.js reaches these through this object instead of importing this file back (it sets
// globalUserData, which stays here). checkFollowUpDue comes through here too: opPanel.js imports
// this file, and shellBoot.js importing it would add a second cycle.
setShellBootDeps({
  get globalUserData() { return globalUserData; },
  set globalUserData(v) { globalUserData = v; },
  get currentURL() { return currentURL; },
  mainTable: (...a) => mainTable(...a),
  populateJobList: (...a) => populateJobList(...a),
  initializeDataListener: (...a) => initializeDataListener(...a),
  populateVendorLists: (...a) => populateVendorLists(...a),
  checkFollowUpDue: (...a) => checkFollowUpDue(...a),
});

// shellAuth.js reaches these through this object instead of importing this file back (it sets
// userRole and currentJobView, which stay here).
setShellAuthDeps({
  get userRole() { return userRole; },
  set userRole(v) { userRole = v; },
  get currentJobView() { return currentJobView; },
  set currentJobView(v) { currentJobView = v; },
  offlineFlow: (...a) => offlineFlow(...a),
  logoutUser: (...a) => logoutUser(...a),
  onlineFlow: (...a) => onlineFlow(...a),
  switchView: (...a) => switchView(...a),
});

// woTable.js reaches these through this object instead of importing this file back.
setWoTableDeps({
  get haulStatus() { return haulStatus; },
  get events() { return events; },
  get latestReceivingData() { return latestReceivingData; },
  get latestCustomerDataById() { return latestCustomerDataById; },
  get countObject() { return countObject; },
  get currentURL() { return currentURL; },
  get workOrderOpTypes() { return workOrderOpTypes; },
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  get userRole() { return userRole; },
  getTechId: (...a) => getTechId(...a),
  jumpToWorkOrder: (...a) => jumpToWorkOrder(...a),
  updateJobInfo: (...a) => updateJobInfo(...a),
  updateJobRow: (...a) => updateJobRow(...a),
  searchCall: (...a) => searchCall(...a),
  searchJobs: (...a) => searchJobs(...a),
  getCustomerDataById: (...a) => getCustomerDataById(...a),
  openWoInvoicePreview: (...a) => openWoInvoicePreview(...a),
  removeOperationDetails: (...a) => removeOperationDetails(...a),
  glowJobRows: (...a) => glowJobRows(...a),
  selectWorkOrder: (...a) => selectWorkOrder(...a),
  opCheck: (...a) => opCheck(...a),
});

// Standalone duplicate folder — single Firebase project, no prod/test branching.
const currentURL = DB_URL;

// laborParts.js reaches these through this object instead of importing this file back.
setLaborPartsDeps({
  get selectedJobRow() { return selectedJobRow; },
  get activeTableRow() { return activeTableRow; },
  get currentURL() { return currentURL; },
  fetchDataFromStore: (...a) => fetchDataFromStore(...a),
  formatDate: (...a) => formatDate(...a),
  partsList: (...a) => partsList(...a),
  updateJobStatus: (...a) => updateJobStatus(...a),
  glowJobRows: (...a) => glowJobRows(...a),
});

// Estimates live in estimates.js; it reaches these through this object rather than importing
// app.js back. Getters, because most of these are declared further down the file.
setEstimateDeps({
  get currentURL() { return currentURL; },
  get globalUserData() { return globalUserData; },
  get letterheadAddress() { return ESTIMATE_LETTERHEAD_ADDRESS; },
  getCurrentUserData: (...a) => getCurrentUserData(...a),
  waitForAuthUser: (...a) => waitForAuthUser(...a),
  reduceInventory: (...a) => reduceInventory(...a),
  addPartsFirebase: (...a) => addPartsFirebase(...a),
  updateOpenDataDB: (...a) => updateOpenDataDB(...a),
  getWorkOrderNumberMove: (...a) => getWorkOrderNumberMove(...a),
  addJobFirebase: (...a) => addJobFirebase(...a),
  fetchDataFromStore: (...a) => fetchDataFromStore(...a),
});

// techWeek.js reaches these through this object instead of importing this file back.
setTechWeekDeps({
  get globalUserData() { return globalUserData; },
  get events() { return events; },
  getTechId: (...a) => getTechId(...a),
  getFormattedDate: (...a) => getFormattedDate(...a),
  updateJobInfo: (...a) => updateJobInfo(...a),
});

// woFilters.js reaches these through this object instead of importing this file back.
setWoFilterDeps({
  get jobsByWorkOrder() { return jobsByWorkOrder; },
  get latestReceivingData() { return latestReceivingData; },
  searchCall: (...a) => searchCall(...a),
  searchJobs: (...a) => searchJobs(...a),
});

// notesPanel.js reaches these through this object instead of importing this file back.
setNotesPanelDeps({
  checkNoteCount: (...a) => checkNoteCount(...a),
  getJobData: (...a) => getJobData(...a),
  getCurrentUserData: (...a) => getCurrentUserData(...a),
  getFormattedDate: (...a) => getFormattedDate(...a),
});

// quickSelect.js reaches these through this object instead of importing this file back.
setQuickSelectDeps({
  addParts: (...a) => addParts(...a),
});

// newCustomer.js reaches these through this object instead of importing this file back.
setNewCustomerDeps({
  get currentURL() { return currentURL; },
  fetchCustomerDataAndGenerateTable: (...a) => fetchCustomerDataAndGenerateTable(...a),
  callNewCustomerAdded: (...a) => callNewCustomerAdded(...a),
  fetchDataFromStore: (...a) => fetchDataFromStore(...a),
});

// opCodePicker.js reaches these through this object instead of importing this file back.
setOpCodePickerDeps({
  get opTypeOrderCache() { return opTypeOrderCache; },
  get SORT_ORDER_FALLBACK() { return SORT_ORDER_FALLBACK; },
  get jobAssist() { return jobAssist; },
  ensureOpTypeOrderCache: (...a) => ensureOpTypeOrderCache(...a),
  wakeShrinkwrapCard: (...a) => wakeShrinkwrapCard(...a),
});

// deviceSync.js reaches these through this object instead of importing this file back.
setDeviceSyncDeps({
  fetchCustomerDataAndGenerateTable: (...a) => fetchCustomerDataAndGenerateTable(...a),
});

// woListActions.js reaches these through this object instead of importing this file back.
setWoListActionDeps({
  createOperationRow: (...a) => createOperationRow(...a),
  opCheck: (...a) => opCheck(...a),
  getAllCustomerData: (...a) => getAllCustomerData(...a),
});

// partsKits.js reaches these through this object instead of importing this file back.
setPartsKitDeps({
  getCustomerDataById: (...a) => getCustomerDataById(...a),
  createPopup: (...a) => createPopup(...a),
  closePopup: (...a) => closePopup(...a),
  addPartsKitToJob: (...a) => addPartsKitToJob(...a),
  formatPhoneNumber: (...a) => formatPhoneNumber(...a),
  showDescriptionPopup: (...a) => showDescriptionPopup(...a),
});

// dailyLog.js reaches these through this object instead of importing this file back.
setDailyLogDeps({
  opCheck: (...a) => opCheck(...a),
  getTechId: (...a) => getTechId(...a),
});

// storageContracts.js reaches these through this object instead of importing this file back.
setShrinkwrapViewDeps({ opCheck: (...a) => opCheck(...a) });
setInternalViewDeps({ opCheck: (...a) => opCheck(...a), openWorkOrder: wo => jumpToWorkOrder(wo) });

setStorageContractDeps({
  get currentURL() { return currentURL; },
  get cachedBusinessProfile() { return cachedBusinessProfile; },
  get ESTIMATE_LETTERHEAD_ADDRESS() { return ESTIMATE_LETTERHEAD_ADDRESS; },
  get STORAGE_PRICING_FALLBACK() { return STORAGE_PRICING_FALLBACK; },
  get storageContractSettings() { return storageContractSettings; },
  formatPhoneNumber: (...a) => formatPhoneNumber(...a),
  getTodaysDate: (...a) => getTodaysDate(...a),
  selectCustomerAndBoat: (...a) => selectCustomerAndBoat(...a),
  storageTierFor: (...a) => storageTierFor(...a),
  addJob: (...a) => addJob(...a),
  getWorkOrderNumber: (...a) => getWorkOrderNumber(...a),
  getCustomerRecord: (...a) => getCustomerRecord(...a),
});

// billing.js reaches these through this object instead of importing this file back.
setBillingDeps({
  getTodaysDate: (...a) => getTodaysDate(...a),
  getCustomerDataById: (...a) => getCustomerDataById(...a),
  removeOperationDetails: (...a) => removeOperationDetails(...a),
  lookupDockmasterInvoiceForWorkOrder: (...a) => lookupDockmasterInvoiceForWorkOrder(...a),
  buildOpContextText: (...a) => buildOpContextText(...a),
  isMiscPartNumber: (...a) => isMiscPartNumber(...a),
  fetchDataFromStore: (...a) => fetchDataFromStore(...a),
});

// ---------- Business branding (Settings > Business tab) ----------
// /settings/businessProfile is deliberately public-read (see database.rules.json) —
// the sidebar, splash screen and login modal below all render before any auth token
// exists, so this runs independent of onAuthStateChanged/auth entirely. The last
// profile seen is kept on the device (brandCache.js) so the splash and login paint
// the business's name/logo instantly on the next visit.
let cachedBusinessProfile = cachedBrand();

/** "Name · address · phone · email" for estimate/invoice/contract letterheads. */
function letterheadLine(p) {
  return [p?.name, p?.address, p?.phone, p?.supportEmail].filter(Boolean).join(' · ');
}

async function applyBusinessBranding() {
  try {
    const snap = await get(ref(database, '/settings/businessProfile'));
    const profile = snap.exists() ? snap.val() : null;
    if (!profile) {
      // No profile configured: show the stock logo rather than nothing.
      document.querySelectorAll('[data-brand="logo"]').forEach(el => { el.style.visibility = 'visible'; });
      return;
    }
    cachedBusinessProfile = profile;
    if (profile.name) document.title = profile.name;
    // Rail, splash, login modal and the storage contract's name spans are all
    // [data-brand] elements.
    rememberBrand(profile);
    ESTIMATE_LETTERHEAD_ADDRESS = letterheadLine(profile);
  } catch (error) {
    console.error('Error applying business branding:', error);
  }
}
applyBusinessBranding();

function setSafeViewportUnit(){
  const vh=(window.visualViewport?window.visualViewport.height:window.innerHeight)*0.01;
  document.documentElement.style.setProperty('--vh',`${vh}px`);
}
function adjustHeight(){
  const navbarHeight=document.querySelector('.navbar').offsetHeight;
  const searchHeight=document.querySelector('.search-section').offsetHeight;
  const extra=40;
  document.documentElement.style.setProperty('--offset',`${navbarHeight+searchHeight+extra}px`);
}

function attachNavHandlers () {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-target]');
    if (!btn) return;                // click was not on a nav button

    const pageId = btn.dataset.target;   // e.g. "maps"
    showPage(pageId);

    // optional: keep the header text in sync
    const label = btn.dataset.label || btn.textContent.trim();
    const current = document.getElementById('current-page');
    if (current) current.textContent = label;
  });
}

function getIframeWindow(id) {
  const host = document.getElementById(id);
  if (!host) return null;

  // If the iframe isn't there yet, build it silently
  let frame = host.querySelector('iframe');
  if (!frame) {
    const src = host.dataset.lazySrc;
    frame = document.createElement('iframe');
    frame.src = src;
    frame.loading = 'lazy';
    frame.className = 'page-frame';
    frame.style.display = 'none';        // keep it hidden
    host.appendChild(frame);
  }
  return frame.contentWindow;
}

const VERSION = process.env.VERSION;
console.log('App version:', VERSION);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const swUrl = `/service-worker.js`;
    navigator.serviceWorker.register(swUrl)
      .then(registration => {
        // Listen for updates to the service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('New SW found. Installing...');
          newWorker.addEventListener('statechange', () => {
            console.log('New SW state:', newWorker.state);
            if (newWorker.state === 'activated') {
              if (navigator.serviceWorker.controller) {
                setTimeout(() => {
                  showUpdatePrompt();
                }, 5000);
              } else {
                console.log('Content is cached for offline use.');
              }
            }
          });
        });

        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', event => {
          if (event.data && event.data.type === 'RELOAD_PAGE') {
            console.log('Reload triggered by service worker update');
            caches.keys().then(cacheNames => {
              return Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
              );
            }).then(() => {
              window.location.reload();
            });
          }
        });

        registration.update();
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // Check every hour
      })
      .catch(error => {
        console.error('SW registration failed:', error);
      });
  });
}






let indexedVersion = 39;
let currentIndexedVersion;

const MOBILE_BREAKPOINT = 600;

const clerkIdInfo = {
  "Priya": "pn",
  "Wanda": "wr",
  "Grant": "gw",
  "Nolan": "nf",
  "Isla": "if"
};

let globalUserData;
let userRole = null;
let globalJobId;
let haulStatus = {};
let workOrderOpTypes = {};
let currentJobView = 'workOrder';
let workOrderJobIDs = {};
let jobsByWorkOrder = null;   // in-memory map built after mainTable() loads IDB data

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/* window.globalUserData = globalUserData;
window.globalTechList = globalTechList;
window.globalTechStats = globalTechStats; */

//Colors
const completeRed = '#ffcdd2';
const progressYellow = '#ecedaf';

var globalCustomerData = {};
let techInfo = {};
let activeTableRow;
let selectedJobRow;
var globalWorkOrder;
let activeRow = null;
let currentOpCodes = [];

// processOpCodes()'s op-type sort order, formerly a hard-coded object local to that function.
// Now sourced from /settings/opTypeOrder (editable via Settings → Op codes → Categories), with this
// as the fallback until that path is ever populated (see the Op Codes admin tab handoff).
const SORT_ORDER_FALLBACK = {
  "General Labor": ["Mechanical", "Hauling", "Cleaning", "Rigging", "Outdrive", "Bottom Paint", "Canvas", "Winterizing", "Shrinkwrap", "Commissioning", "Moorings", "Internal Yard", "Internal Moorings"],
  Winterizing: ["Winterizing", "Shrinkwrap", "Rigging", "Hauling", "Mechanical", "Cleaning", "Outdrive", "Bottom Paint", "Canvas", "Commissioning", "Moorings", "Internal Yard", "Internal Moorings"],
  "Spring Commissioning": ["Commissioning", "Rigging", "Cleaning", "Hauling", "Bottom Paint", "Mechanical", "Shrinkwrap", "Canvas", "Outdrive", "Winterizing", "Moorings", "Internal Yard", "Internal Moorings"],
  Internal: ["Internal Yard", "Internal Moorings", "Mechanical", "Hauling", "Cleaning", "Rigging", "Outdrive", "Bottom Paint", "Canvas", "Winterizing", "Shrinkwrap", "Commissioning", "Moorings"],
  Moorings: ["Moorings", "Internal Moorings", "Mechanical", "Hauling", "Cleaning", "Rigging", "Outdrive", "Bottom Paint", "Canvas", "Winterizing", "Shrinkwrap", "Commissioning", "Internal Yard"],
};
let opTypeOrderCache = null;
let opTypeOrderFetchPromise = null;
function ensureOpTypeOrderCache() {
  if (opTypeOrderCache || opTypeOrderFetchPromise) return opTypeOrderFetchPromise;
  opTypeOrderFetchPromise = get(ref(database, '/settings/opTypeOrder')).then(snap => {
    opTypeOrderCache = snap.val() || null;
  }).catch(() => { opTypeOrderCache = null; opTypeOrderFetchPromise = null; });
  return opTypeOrderFetchPromise;
}
var globalShrink;
let countObject = {};
// Outstanding job-linked purchase-order lines, shaped to match what
// wo-ops-aggregation.js's outstandingPartsForJob()/resolveAttentionSlot()/
// resolveOperationSubline() already expect (Job_ID, Quantity_Remaining,
// Part_Number, Expected_Date, Date) — built by flattenOutstandingPOLines()
// from /inventoryPOs, NOT the old /receiving store. Loaded once per
// Work-Orders/Operations render pass and reused by realtime row refreshes so
// they don't each need their own fetch. See wo-ops-aggregation.js and the
// wo_ops_list_redesign_built memory for why this isn't /receiving anymore —
// that store's one review UI (receiving.html) has no reachable live entry
// point (a different, unrelated redesign replaced it with the PO-based
// Receiving tab in inventory.js, which has no notion of Job_ID at all until
// this change added one).
let latestReceivingData = [];

// Turns open /inventoryPOs records into the flat shape the aggregation
// module already reads, so wo-ops-aggregation.js itself needed no changes.
// Only lines with a Job_ID matter here — bulk/reorder-tab POs (general
// restocking, no specific job) are correctly invisible to this feature.
function flattenOutstandingPOLines(poData, partsData) {
  const partsById = new Map((partsData || []).map(p => [p.id, p]));
  const rows = [];
  (poData || []).forEach(po => {
    if (!po.open) return; // closed/received — nothing left outstanding
    (po.lines || []).forEach((line, idx) => {
      if (!line.Job_ID) return;
      const part = partsById.get(line.partId);
      const remaining = lineDue(po, idx);
      if (remaining <= 0) return;
      rows.push({
        id: `${po.id}:${idx}`,
        Job_ID: line.Job_ID,
        Part_Number: part?.Part_Number || '',
        Quantity_Remaining: remaining,
        Expected_Date: line.Expected_Date || '',
        Date: po.placed || '',
        Vendor: po.vendor || '',
      });
    });
  });
  return rows;
}

// Loads /inventoryPOs + /inventory (both already synced by getInventoryPOs()/
// the app's normal inventory sync) and flattens them — the replacement for
// the old single fetchDataFromStore('receivingData') call.
async function loadOutstandingJobPartOrders() {
  const [poData, partsData] = await Promise.all([
    fetchDataFromStore('inventoryPOs').catch(() => []),
    fetchDataFromStore('inventoryData').catch(() => []),
  ]);
  return flattenOutstandingPOLines(poData, partsData);
}
// customerData keyed by Customer_ID, loaded once per Operations-tab render
// pass (populateJobList()/closedJobList()) so createOperationRow() can read
// Boat_Length/Boat_Make synchronously instead of an IndexedDB get() per row.
let latestCustomerDataById = new Map();



let events = [];

document.addEventListener('DOMContentLoaded',()=>{
  setSafeViewportUnit();
  adjustHeight();
  attachNavHandlers(); 
});
window.addEventListener('resize',()=>{
  setSafeViewportUnit();
  adjustHeight();
});
if(window.visualViewport){
  visualViewport.addEventListener('resize',()=>{
    setSafeViewportUnit();
    adjustHeight();
  });
}


async function formatDate(date) {
  try {
    // Local calendar date: with no argument this is "today", which UTC getters
    // turned into tomorrow every evening (decline/delete/part-order stamps).
    date = date ? parseDay(date) : new Date();

    let year = date.getFullYear();
    let month = (date.getMonth() + 1).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');
    let formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  } catch (error) {
    console.error('Error formatting date', error);
  }
}


function formatPhoneNumber(phone) {
  // Guards a pre-existing crash risk: every call site (D1 picker, this
  // function's own callers) passes customer.Phone straight through, and a
  // customer without one made this throw before reaching any `|| ''` the
  // caller wrapped around the result.
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');

  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phone;
}

/*   let dbPromise = null;
  

  function openDatabase() {
    // If we've already opened or are opening this database, reuse that same Promise.
    if (dbPromise) return dbPromise;
  
    dbPromise = new Promise((resolve, reject) => {
      // Attempt to open (or upgrade) to `indexedVersion`.
        const versionToUse =
        (typeof currentIndexedVersion !== 'undefined' && currentIndexedVersion)
        ? currentIndexedVersion
        : indexedVersion;

        const request = indexedDB.open('openDatabase', versionToUse);

  
      // Fires if the DB is new or its current version is lower than `indexedVersion`.
      request.onupgradeneeded = (event) => {
        console.log('[IndexedDB] onupgradeneeded – upgrading to version', versionToUse);
        const db = event.target.result;
        const oldVersion = event.oldVersion;
        console.log(`Upgrading from version ${oldVersion} to ${db.version}...`);
  
        // Create all needed object stores (if they don't already exist).
        if (!db.objectStoreNames.contains('customerData')) {
          db.createObjectStore('customerData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('jobData')) {
          db.createObjectStore('jobData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('closedJobs')) {
          db.createObjectStore('closedJobs', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('opCodeData')) {
          db.createObjectStore('opCodeData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('mapDataBoats')) {
          db.createObjectStore('mapDataBoats', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('mapDataMoorings')) {
          db.createObjectStore('mapDataMoorings', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('inventoryData')) {
          db.createObjectStore('inventoryData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('receivingData')) {
          db.createObjectStore('receivingData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('mooringDatabase')) {
          db.createObjectStore('mooringDatabase', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('rentalDatabase')) {
          db.createObjectStore('rentalDatabase', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('vendorData')) {
          db.createObjectStore('vendorData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('scheduleData')) {
          db.createObjectStore('scheduleData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('techData')) {
          db.createObjectStore('techData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('notificationData')) {
          db.createObjectStore('notificationData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
  
        const tx = event.target.transaction;
const metadataStore = tx.objectStore('metadata');

function ensureMetadataKey(key) {
  const getReq = metadataStore.get(key);
  getReq.onsuccess = () => {
    if (!getReq.result) metadataStore.put({ key, timestamp: 0 });
  };
}

ensureMetadataKey('customerDataLastUpdated');
ensureMetadataKey('jobDataLastUpdated');
ensureMetadataKey('closedJobsLastUpdated');
ensureMetadataKey('opCodeDataLastUpdated');
ensureMetadataKey('boatDataLastUpdated');
ensureMetadataKey('mooringDataLastUpdated');
ensureMetadataKey('inventoryDataLastUpdated');
ensureMetadataKey('receivingDataLastUpdated');
ensureMetadataKey('mooringDatabaseLastUpdated');
ensureMetadataKey('rentalDatabaseLastUpdated');
ensureMetadataKey('vendorDataLastUpdated');
ensureMetadataKey('scheduleDataLastUpdated');
ensureMetadataKey('techDataLastUpdated');
ensureMetadataKey('settingsLastUpdated');

      };
  
      // Fires when the DB is successfully opened (and after onupgradeneeded if needed).
      request.onsuccess = (event) => {
        const db = event.target.result;
        resolve(db);
      };
  
      // Fires on error (e.g., user blocking IndexedDB usage).
      request.onerror = (event) => {
        console.error('[IndexedDB] open error:', event.target.error);
        reject(event.target.error);
      };
    });
  
    return dbPromise;
  } */
  
async function getCurrentDatabaseVersion() {
  try {
    const db = await getDB();
    return db.version;
  } catch (error) {
    console.error('Error checking database version:', error);
    return null;
  }
}


async function opCodeCheck(jobId) {

  const jobData = await getJobData(jobId);

  const opCode = jobData.OpCode;
  const opCodeDataBase = ref(database, 'opcodes'); // Reference to /opcode

  try {
    // Fetch the data for the given opCode
    const snapshot = await get(child(opCodeDataBase, opCode));
    if (snapshot.exists()) {
      return snapshot.val(); // Return the data if it exists
    } else {
      return null; // Return null if the opcode doesn't exist
    }
  } catch (error) {
    console.error("Error fetching opcode data:", error);
    return null; // Handle errors gracefully
  }
}


onAuthStateChanged(auth, handleAuthStateChange);

checkAuthState();


window.addEventListener('online', () => {
  setAppMode({ readOnly: false });
  const user = auth.currentUser;
  if (user) onlineFlow(user);
});

window.addEventListener('offline', () => {
  setAppMode({ readOnly: true });
  const user = auth.currentUser;
  if (user) offlineFlow(user);
});

// Drive read-only mode from Firebase connectivity (more reliable than navigator.onLine)
onFirebaseConnectedChange((connected) => {
  const user = auth.currentUser;
  if (connected) {
    setAppMode({ readOnly: false });
    if (user) onlineFlow(user);
  } else {
    setAppMode({ readOnly: true });
  }
});


async function renderJobsInChunks(jobsArray, jobListDiv, view, isStale = () => false) {

  let chunkSize = 100;
  for (let i = 0; i < jobsArray.length; i += chunkSize) {
    if (isStale()) return; // a newer draw took over the list between chunks
    const fragment = document.createDocumentFragment();
    const chunk = jobsArray.slice(i, i + chunkSize);

    chunk.forEach((job) => {
      const jobDiv = createOperationRow(job, view);
      fragment.appendChild(jobDiv);
    });

    jobListDiv.appendChild(fragment);
/*     jobListDiv.removeEventListener('scroll', throttle(onScroll, 200)); */
    await new Promise(requestAnimationFrame);
  }
}

async function lazyLoadJobs(jobsArray, jobListDiv, view) {
  
  let chunkSize = 100;
  let currentIndex = 0;

  const reversedJobsArray = [...jobsArray].reverse();


  function loadNextChunk() {
    if (currentIndex >= reversedJobsArray.length) return;

    const fragment = document.createDocumentFragment();
    const chunk = reversedJobsArray.slice(currentIndex, currentIndex + chunkSize);

    chunk.forEach((job) => {
      const jobDiv = createOperationRow(job, view);
      fragment.appendChild(jobDiv);
    });

    jobListDiv.appendChild(fragment);
    currentIndex += chunkSize;
  }

  // Initial load
  loadNextChunk();

  // One scroll loader per list: each reload used to add another, so old loaders kept appending
  // their stale (unfiltered) chunks into search results and doubled rows after a few searches.
  // The box is read at scroll time: search results are complete, so nothing more loads under them.
  async function onScroll() {
    const { scrollTop, scrollHeight, clientHeight } = jobListDiv;

    if ( (scrollTop + clientHeight >= scrollHeight - 10) && document.getElementById('closed-search').value.trim() === '') {
      loadNextChunk();
    }
  }

  if (jobListDiv._lazyScroll) jobListDiv.removeEventListener('scroll', jobListDiv._lazyScroll);
  jobListDiv._lazyScroll = throttle(onScroll, 200);
  jobListDiv.addEventListener('scroll', jobListDiv._lazyScroll);
}

async function populateJobList() {
  try {
    const db = await getDB();

    const tx = db.transaction(['jobData'], 'readonly');
    const store = tx.objectStore('jobData');
    const jobListDiv = document.getElementById('jobList');
    const jobs = [];

    await new Promise((resolve, reject) => {
      const req = store.openCursor();

      req.onerror = () => reject(req.error);

      req.onsuccess = (e) => {
        const c = e.target.result;
        if (!c) return resolve();

        if (c.key !== 'lastUpdated') {
          const j = c.value;
          if (j && !['Declined', 'Closed'].includes(j.Status)) jobs.push(j);
        }

        c.continue();
      };
    });

    jobListDiv.innerHTML = '';

    const [receivingData, customerRecords] = await Promise.all([
      loadOutstandingJobPartOrders(),
      fetchDataFromStore('customerData').catch(() => [])
    ]);
    latestReceivingData = receivingData;
    latestCustomerDataById = new Map(customerRecords.map(c => [c.id, c]));

    const sorted = jobs.sort((a, b) => new Date(a.Start_Date) - new Date(b.Start_Date));
    await renderJobsInChunks(sorted, jobListDiv);

    if (!jobListDiv.dataset.opClickBound) {
      jobListDiv.addEventListener('click', throttle(opCheck, 200));
      jobListDiv.dataset.opClickBound = '1';
    }

    if (!sorted.length) {
      jobListDiv.innerHTML = emptyHTML({ title: 'No open operations', body: 'Operations appear here once they’re added to a work order.' });
    }
  } catch (err) {
    console.error('populateJobList:', err);
    listLoadFailed('jobList', 'operations', err);
  }
}


// Switches to the Work Orders tab, scrolls to, and expands the given work
// order's row — the Operations-tab work-order chip's only behavior (README
// §Work order column: "the only tie back").
// Equipment, optionally opened on one unit's page (filter: { unit }).
async function openEquipmentPage(filter) {
  await showPage('equipment');
  document.getElementById('current-page').textContent = 'Equipment';
  if (!filter) return;
  const iframe = document.getElementById('equipment')?.querySelector('iframe');
  const post = () => iframe?.contentWindow?.postMessage({ type: 'dashboardFilter', filter }, window.location.origin);
  let loaded = false;
  try { loaded = iframe.contentDocument?.readyState === 'complete' && iframe.contentWindow.location.href !== 'about:blank'; } catch { loaded = false; }
  if (loaded) post(); else iframe?.addEventListener('load', post, { once: true });
}

async function jumpToWorkOrder(workOrder) {
  await switchView('workOrder');
  let row = document.querySelector(`.table-item[data-workorder="${CSS.escape(String(workOrder))}"]`);
  if (!row && document.getElementById('table-content')?.children.length === 0) {
    await mainTable();
    row = document.querySelector(`.table-item[data-workorder="${CSS.escape(String(workOrder))}"]`);
  }
  if (!row) { toastMessage(`Work order ${workOrder} isn’t in the open list`, '#0C2C49'); return; }
  // Not 'smooth': opening the row adds a close-when-scrolled-out-of-view
  // listener, and a smooth scroll still in flight would trip it at once.
  row.scrollIntoView({ block: 'center' });
  if (!row.classList.contains('active-work-order')) {
    row.click();
  }
}


function showEstimatedHoursPopup(jobData, row) {
  return new Promise((resolve, reject) => {
  const popup = document.getElementById('estimatedHoursPopup');
  const input = document.getElementById('estimatedHoursInput');
  const submitButton = document.getElementById('submitEstimatedHours');
  const cancelButton = document.getElementById('cancelEstimatedHours');

  popup.style.display = 'flex';

  submitButton.onclick = async () => {
    const hours = parseFloat(input.value);
    console.log(hours);
    if (hours > 0) {
      await updateJobInfo(jobData.Job_ID, 'Estimated_Hours', hours);
      const rowData = JSON.parse(row.getAttribute('data-jobInfo'));
      rowData.Estimated_Hours = hours;
      row.setAttribute('data-jobInfo', JSON.stringify(rowData));
      popup.style.display = 'none';
      input.value = '';
      resolve();
    } else {
      console.log('Please enter a valid number of hours.');
    }
  };

  input.addEventListener('keypress', async (e) => {
    if (e.key === 'Enter') {
      const hours = parseFloat(input.value);
    console.log(hours);
    if (hours > 0) {
      await updateJobInfo(jobData.Job_ID, 'Estimated_Hours', hours);
      const rowData = JSON.parse(row.getAttribute('data-jobInfo'));
      rowData.Estimated_Hours = hours;
      row.setAttribute('data-jobInfo', JSON.stringify(rowData));
      popup.style.display = 'none';
      input.value = '';
      resolve();
    } else {
      console.log('Please enter a valid number of hours.');
    }
    }
  });

  cancelButton.onclick = () => {
    popup.style.display = 'none';
    input.value = ''; 
    reject('User cancelled');
  };
  });
}







async function updateJobDatesBasedOnBoatId(boatId, index, value) {

  try {
    const elements = document.querySelectorAll('[data-boatid="' + boatId + '"]');

    for (const element of elements) {
      const jobData = JSON.parse(element.getAttribute('data-jobInfo') || '{}');

      if (index === 'Haul_Date') {
        jobData.Haul_Date = value;
        if (jobData.Category === 'Winterizing') jobData.Start_Date = value;
      }

      if (index === 'Launch_Date') {
        jobData.Launch_Date = value;
        if (jobData.Category === 'Spring Commissioning') jobData.Start_Date = value;
      }

      if (element.classList.contains('job-item')) {
        if (index === 'Haul_Date' && jobData.Category === 'Winterizing') {
          await updateJobInfo(jobData.Job_ID, 'Haul_Date', value);
          await updateJobInfo(jobData.Job_ID, 'Start_Date', value);
        }

        if (index === 'Launch_Date' && jobData.Category === 'Spring Commissioning') {
          await updateJobInfo(jobData.Job_ID, 'Launch_Date', value);
          await updateJobInfo(jobData.Job_ID, 'Start_Date', value);
        }

        const dateElement = element.querySelector('.start-date-info');
        const requestDateLabel = element.querySelector('.request-date-label');
        let jobDate = '';

        if (jobData.Schedule_Date && jobData.Schedule_Date !== '') {
          jobDate = await formatDate(jobData.Schedule_Date);
        } else if (jobData.Start_Date && jobData.Start_Date !== '') {
          jobDate = await formatDate(jobData.Start_Date);
        } else if (jobData.Request_Date && jobData.Request_Date !== '') {
          if (jobData.Category === 'Winterizing' && (jobData.Haul_Date ?? '') === '') {
            jobDate = (await formatDate(jobData.Request_Date)) + ' (No Haul Date)';
            if (requestDateLabel) {
              requestDateLabel.textContent = 'Request Date: ';
              requestDateLabel.style.color = 'red';
            }
          } else if (jobData.Category === 'Spring Commissioning' && (jobData.Launch_Date ?? '') === '') {
            jobDate = (await formatDate(jobData.Request_Date)) + ' (No Launch Date)';
            if (requestDateLabel) {
              requestDateLabel.textContent = 'Request Date: ';
              requestDateLabel.style.color = 'red';
            }
          } else {
            jobDate = await formatDate(jobData.Request_Date);
          }
        }

        if (dateElement) dateElement.innerHTML = jobDate;
        element.setAttribute('data-jobInfo', JSON.stringify(jobData));
      }

      if (element.classList.contains('table-item')) {
        element.setAttribute('data-jobInfo', JSON.stringify(jobData));
        // Haul_Date/Launch_Date changes affect the "Waiting on the haul/launch"
        // attention-slot rule, so a full recompute (not just a date-span
        // patch) is what actually needs to happen here.
        if (jobData.Work_Order) refreshWorkOrderRow(jobData.Work_Order);
      }
    }
  } catch (error) {
    console.error('Error updating job data:', error);
  }
}


async function updateJobRow(jobId, key, value, dataSet) {

  try {
    let jobRow = document.querySelectorAll(`.job-item[data-id="${jobId}"]`);

    if (!jobRow) {
        console.error(`Job with ID ${jobId} not found`);
        return;
    }

    jobRow.forEach(row => {

      let currentJobInfo = JSON.parse(row.getAttribute('data-jobInfo'));

      currentJobInfo[key] = value;
      row.setAttribute('data-jobInfo', JSON.stringify(currentJobInfo));
  
      // Update HTML elements
      if (key === 'Customer_Name') {
        row.querySelector('.customer-name-jobs').textContent = value;
      }
      if (key === 'Boat_Name') {
        row.querySelector('.boat-name-jobs').textContent = value;
      }
      if (key === 'Category') {
        row.querySelector('.category-info').textContent = value;
      }
      if (key === 'Work_Order') {
        row.querySelector('.work-order-jobs').innerHTML = `<strong>WO: </strong>${value}`;
      }
      if (key === 'Description') {
        const jobDescription = value.includes(':') ? value.split(':')[0] : currentJobInfo.Short_Description;
        row.querySelector('.description-info p:first-child').textContent = jobDescription;
      }
      if (key === 'Techs' && row.querySelector('.tech-info')) {
        row.querySelector('.tech-info').value = value;
        row.querySelector('.tech-info').setAttribute('data-selected-tech', value);

      }
      if (key === 'Start_Date') {
        const dateElement = row.querySelector('.start-date-info');
          let jobDate;
          jobDate = new Date(value).getFullYear() === new Date().getFullYear() ?
          value.split('-').slice(1).join('-') : value;
          dateElement.innerHTML = `<strong>Start: </strong>${jobDate}`;
      }
      if (key === 'Schedule_Date' && row.querySelector('.schedule-date-info')) {
        const scheduleDateInput = row.querySelector('.schedule-date-info');
        scheduleDateInput.value = value;
        
        if(value === ''){
          let jobDate = currentJobInfo['Start_Date'];
          jobDate = jobDate.split('-')[1] + '/' + jobDate.split('-')[2] + '/' + jobDate.split('-')[0];
          row.querySelector('.start-date-info').innerHTML = `<strong>Start: </strong>${jobDate}`;
        }else{
          let jobDate;
          jobDate = new Date(value).getFullYear() === new Date().getFullYear() ?
          value.split('-')[1] + '/' + value.split('-')[2] + '/' + value.split('-')[0] : value;
          row.querySelector('.start-date-info').innerHTML = `<strong>Start: </strong>${jobDate}`;
        }
        scheduleDateInput.style.color = value ? '' : 'red';
        applySorting();
      }
      if (key === 'Status') {
        row.querySelector('.status-priority-info p:first-child').innerHTML = `<strong>Status: </strong>${value}`;
        const statusIndicator = row.querySelector('.status-info');
        statusIndicator.style.backgroundColor = value === 'In Progress' ? 'yellow' : 'green';
        /* row.style.outline = value === 'In Progress' ? '2px solid yellow' : '2px solid green'; */
      }
      if (key === 'Priority') {
        row.querySelector('.status-priority-info p:last-child').innerHTML = `<strong>Priority: </strong>${value || 'Normal'}`;
      }
      if(key === 'Notes'){
        const jobNotesP = row.querySelector('.notes-info button');
        if (value) {
          jobNotesP.querySelector('img').style.filter = 'brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(6932%) hue-rotate(359deg) brightness(94%) contrast(113%)';
        } else {
          jobNotesP.querySelector('img').style.filter = '';
        }
      }
    })
    
  } catch (error) {
    console.error("Error updating job row:", error);
  }

}

async function mainTable() {
  await load();

  try {
    document.getElementById('table-content').innerHTML = '';
    document.getElementById("table-loading-icon").style.display = "flex";

    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readonly');
    const store = transaction.objectStore('jobData');

    const dataArray = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onerror = (event) => reject(event.target.error || req.error);
      req.onsuccess = (event) => {
        const rows = event.target.result || [];
        resolve(rows.filter(rec => rec.id !== 'lastUpdated'));
      };
    });

    var awaiting = [];
    var order = [];
    var closed = [];
    var operationsByWorkOrder = {};

    // Build in-memory lookup map so displayOperations needs no IDB scan per click
    jobsByWorkOrder = new Map();
    dataArray.forEach(data => {
      const workOrderNumber = data['Work_Order'];
      const status = data['Status'];

      if (!operationsByWorkOrder[workOrderNumber]) {
        operationsByWorkOrder[workOrderNumber] = [];
      }

      operationsByWorkOrder[workOrderNumber].push(status);

      if (data.Category?.toString() !== 'Declined') {
        if (!jobsByWorkOrder.has(workOrderNumber)) jobsByWorkOrder.set(workOrderNumber, []);
        jobsByWorkOrder.get(workOrderNumber).push(data);
      }

      if (data.Category && data.Status !== 'Declined') {
        if (!workOrderOpTypes[data.Work_Order]) {
          workOrderOpTypes[data.Work_Order] = [];
        }
        workOrderOpTypes[data.Work_Order].push(data.Op_Type);
      }

      if (data['Techs'] && (data['Status'] === 'On Work Schedule' || data['Status'] === 'In Progress')) {
        if (!techInfo[data['Techs']]) {
          techInfo[data['Techs']] = [];
        }
        techInfo[data['Techs']].push([
          data['Job_ID'],
          data['Customer_Name'],
          data['Boat_Name'],
          data['Work_Order'],
          data['Description'],
          data['Status'],
          data['Notes'],
          data['Start_Date'],
          data['Transfer_to_Billing']
        ]);
      }
    });

    countObject = dataArray.reduce((accumulator, item) => {
      const workOrder = item.Work_Order;
      const dependency = item.Operation_Dependency;

      if (!accumulator[workOrder]) {
        accumulator[workOrder] = {
          Count: 0,
          Dependency: 0
        };
      }

      if (item.Status && item.Description) {
        accumulator[workOrder].Count += 1;
        accumulator[workOrder].Dependency += Number(dependency);
        accumulator[workOrder].Customer_Name = item.Customer_Name;
        accumulator[workOrder].Customer_ID = item.Customer_ID;
      }

      return accumulator;
    }, {});

    const workOrderStatuses = await setUpWorkOrderStatuses(operationsByWorkOrder);

    // Loaded once per render pass (small — outstanding orders only matter),
    // same pattern as jobsByWorkOrder above. Feeds the attention-slot part-
    // order rule on every row with no per-row fetch.
    const receivingData = await loadOutstandingJobPartOrders();
    latestReceivingData = receivingData;

    var wo = dataArray.map(data => data['Work_Order']);
    var unique = [...new Set(wo)];

    var woList = [];

    for (let workOrder of unique) {
      let jobsForWorkOrder = dataArray.filter(data => String(data['Work_Order']).trim() === String(workOrder).trim());

      // Find the first non-declined job for this work order
      let nonDeclinedJob = jobsForWorkOrder.find(job => job['Status'] !== 'Declined');
      if (nonDeclinedJob) {
        woList.push(nonDeclinedJob);
      }
    }

    // Show progress bar for the render pass
    const progressWrap = document.getElementById('table-progress-wrap');
    const progressBar = document.getElementById('table-progress-bar');
    if (progressWrap && progressBar) {
      progressBar.style.width = '0%';
      progressWrap.style.display = 'flex';
    }

    await renderTable(woList, workOrderStatuses, receivingData);

    document.querySelectorAll('.operation-container-row').forEach((element) => {
      element.remove();
    });

    if (!woList.length) {
      document.getElementById('table-content').innerHTML = emptyHTML({
        title: 'No open work orders',
        body: 'New work orders show up here as soon as they’re created. Closed ones are under Closed.',
        action: { label: 'New work order', attr: 'data-ui-new-wo' },
      });
    }
  } catch (error) {
    console.error('Error fetching job data:', error);
    listLoadFailed('table-content', 'work orders', error);
  }
}

// A list that couldn't be read from this device's copy of the data: say so, keep the
// person's place, offer Try again (which re-runs the whole load).
function listLoadFailed(listId, what, error) {
  document.getElementById('table-loading-icon').style.display = 'none';
  const pw = document.getElementById('table-progress-wrap');
  if (pw) pw.style.display = 'none';
  const el = document.getElementById(listId);
  if (!el) return;
  el.innerHTML = errorHTML({
    what: `Couldn’t load ${what}`,
    kept: 'Nothing was changed — this only reads the copy of the data kept on this device.',
    next: 'Try again. If it keeps failing, reload the page; the app re-downloads the data.',
    code: errorCode(error),
    retry: { label: 'Try again', attr: 'data-ui-retry' },
  });
}

document.addEventListener('click', e => {
  const eqUnit = e.target.closest('[data-equipment-unit]')?.dataset.equipmentUnit;
  if (eqUnit) { openEquipmentPage({ unit: eqUnit }); return; }
  if (e.target.closest('#table-content [data-ui-retry]')) mainTable();
  else if (e.target.closest('#jobList [data-ui-retry]')) populateJobList();
  else if (e.target.closest('#table-content [data-ui-new-wo]')) document.querySelector('.rail-add-btn')?.click();
});


/* async function setUpWorkOrderStatuses(operationsByWorkOrder){

  try {
    let workOrderStatuses = {};

        Object.keys(operationsByWorkOrder).forEach(workOrderNumber => {
          const operations = operationsByWorkOrder[workOrderNumber];
        
          // Initialize the work order with default status
          let workOrderStatus = {
            Work_Order: workOrderNumber,
            Status: 'Needs Scheduling'
          };
        
          // Determine the status based on the operations
          if (operations.every(status => status === "Closed" || status === "Declined")) {
            // All statuses are "Closed" or "Declined"
            workOrderStatus.Status = 'Closed';
          } else if (operations.every(status => 
            ["Ready to Bill", "Complete", "Closed"].includes(status))) {
            // All statuses are "Ready to Bill", "Complete", or "Closed"
            workOrderStatus.Status = 'Ready to Bill';
          } else if (operations.every(status => 
            ["On Work Schedule", "Estimate", "Declined"].includes(status))) {
            // All statuses are "On Work Schedule", "Estimate", or "Declined"
            workOrderStatus.Status = 'Needs Scheduling';
          } else {
            // Default case for mixed statuses
            workOrderStatus.Status = 'In Progress';
          }
        
          // Store the work order status
          workOrderStatuses[workOrderNumber] = workOrderStatus;
        });
        return workOrderStatuses;
      }catch(error){
        console.error("Error setting up work order statuses:", error);
      }
} */

document.getElementById('log-out-button').addEventListener('click', logoutUser);

document.getElementById('codeSearch').addEventListener('input', () => {
  codeSearch()
})

document.getElementById('search').addEventListener('input', searchCall)

// The form's hidden "Select Customer" button (still .click()ed when no customer is chosen) opens the
// customer/boat picker — the old Customer List and its long New Customer form are retired.
document.getElementById('customerDatabaseButton').addEventListener('click', () => openWoCustomerPicker());


// Keeps the Status select on the operation editor (#Update panel) looking
// like a colored pill instead of a plain gray dropdown — same tones as the
// read-only status pills elsewhere, but kept as a real <select> rather than
// a custom popover so native keyboard/accessibility behavior isn't lost.
function syncUpdateStatusTone() {
  const select = document.getElementById('updateStatus');
  if (!select) return;
  select.className = select.className.replace(/\bop-tone-\S+/g, '').trim();
  select.classList.add('update-form', 'op-tone-' + statusToneSlug(select.value));
}

document.getElementById('updateStatus').addEventListener('change', syncUpdateStatusTone);
syncUpdateStatusTone();

// Ensure (create if needed) the maps iframe and wire up its load handler exactly once.
// Module level (it used to sit inside the DOMContentLoaded handler below), so the
// Schedule page's "show map" message handler can reach it too.
function ensureMapsIframe() {
  const host = document.getElementById('maps');
  if (!host) return null;

  let iframe = host.querySelector('iframe');
  if (!iframe) {
    // Create the iframe and attach the onload BEFORE setting src to avoid races
    iframe = document.createElement('iframe');
    iframe.className = 'page-frame';
    iframe.loading = 'eager';
    iframe.addEventListener('load', () => {
      iframe.dataset.loaded = '1';
      try {
        iframe.contentWindow.postMessage({ action: 'renderMaps' }, '*');
        console.log('[app.js] maps iframe loaded → renderMaps posted');
      } catch (e) {
        console.error('[app.js] postMessage on load failed', e);
      }
    });

    // If you use lazy src on the host container, prefer that:
    const src = host.dataset.lazySrc || '/maps.html';
    iframe.src = src;
    host.appendChild(iframe);
  }
  return iframe;
}

document.addEventListener('DOMContentLoaded', function () {

  // The rail's New (createNew below) is the one way in to the New Work Order form.
  wireNewMenu();

  // Schedule button
  document.getElementById('scheduleButton')?.addEventListener('click', () => {
    document.getElementById('tech-data').style.display = 'flex';
/*     load(); */
  });

  // Calculator button
  document.getElementById('calc-button').addEventListener('click', function () {
    document.getElementById('calcBody').style.display = 'flex';
  });

  // Close button for pop-out
  document.getElementById('close-btn').addEventListener('click', function () {
    // You need to define what should happen when this button is clicked
  });

  // Calendar close button
  document.getElementById('calendar-close-btn').addEventListener('click', function () {
    // You need to define what should happen when this button is clicked
  });

  // Generate general work order button
  document.getElementById('general-work-order-button').addEventListener('click', function () {
    getWorkOrderNumber('general');
  });

  // Generate winter work order button
  document.getElementById('winter-work-order-button').addEventListener('click', function () {
    getWorkOrderNumber('winter');
  });

  // Generate spring work order button
  document.getElementById('spring-work-order-button').addEventListener('click', function () {
    getWorkOrderNumber('spring');
  });

    document.getElementById('shrinkwrap-work-order-button').addEventListener('click', function () {
    getWorkOrderNumber('shrinkwrap');
  });

  // G1 — one click, two sequential internal WO-number assignments; per-card
  // Generate above stays for one-season contracts.
  document.getElementById('generate-both-season-wo-btn')?.addEventListener('click', async function () {
    if (document.getElementById('winterWorkOrder').value === '') await getWorkOrderNumber('winter');
    if (document.getElementById('springWorkOrder').value === '') await getWorkOrderNumber('spring');
  });

  document.getElementById('add-btn').addEventListener('click', (e) => {

    e.stopPropagation();
    e.preventDefault();

    document.getElementById('activeJobs').innerHTML = '';
    document.getElementById('work-order-type-select').value = 'general-work-order'

    // F1 — a fresh "New Work Order" must start collapsed. Without this
    // reset, leaving a *previous* WO's editors panel manually expanded (via
    // "Edit details", never clicking Done) leaked into every subsequent new
    // WO, which then never re-collapsed.
    resetWoEditorsExpanded();
    document.getElementById('wo-editors-panel')?.classList.add('wo-editors-collapsed');
    document.getElementById('addModal')?.classList.remove('wo-create-modal--storage');

    const launchDateCheck = document.getElementById('work-order-form-dates-launch-date').getAttribute('date-updated');

    if (!launchDateCheck && document.getElementById('work-order-form-customer-id').value && document.getElementById('work-order-form-boat-id').value) {
      const customerId = document.getElementById('work-order-form-customer-id').value;
      const boatId = document.getElementById('work-order-form-boat-id').value;
      const index = 'Launch_Date';
      const date = document.getElementById('work-order-form-dates-launch-date').value;

      addHaulLaunchStorageDates(customerId, boatId, index, date)
      document.getElementById('work-order-form-dates-launch-date').removeAttribute('date-updated');
    }

      if(document.getElementById('add-btn').getAttribute('data-return') === 'spring' ){
        document.getElementById('spring').style.display = 'flex';
        document.getElementById('spring').style.visibility = 'visible';

        document.getElementById('open-work').style.display = 'none';
        document.getElementById('open-work').style.visibility = 'hidden';

        document.getElementById('add-btn').removeAttribute('data-return');
        document.getElementById('exitAdd').removeAttribute('data-return');

      }
    

    currentOpCodes = []
    AddRow();

      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      document.getElementById('general-work-order-button').style.display = 'flex'
      document.getElementById('customerDatabaseButton').style.display = 'flex'
      document.getElementById('search').focus()
      document.getElementById("work-order-form-customer-name").value = ""
      document.getElementById("work-order-form-customer-id").value = ""
      document.getElementById("work-order-form-customer-DMid").value = ""
      document.getElementById("work-order-form-customer-street").value = ""
      document.getElementById("work-order-form-customer-city").value = ""
      document.getElementById("work-order-form-customer-state").value = ""
      document.getElementById("work-order-form-customer-zip").value = ""
      document.getElementById("work-order-form-boat-name").value = ""
      document.getElementById("work-order-form-boat-id").value = ""
      document.getElementById("work-order-form-boat-DMid").value = ""
      document.getElementById("generalWorkOrder").value = ""
      document.getElementById("winterWorkOrder").value = ""
      document.getElementById("springWorkOrder").value = ""
      document.getElementById("shrinkwrapWorkOrder").value = ""
      document.getElementById('work-order-form-dates-launch-date').value = nextYear + "-04-01";
      document.getElementById('work-order-form-dates-haul-date').value = ""
      document.getElementById('work-order-form-customer-phone').value = "";
      document.getElementById('work-order-form-customer-email').value = "";
      document.getElementById('work-order-form-storage-confirm').checked = false
      document.getElementById('work-order-form-boat-mooring-number-slip').value = ''
      document.getElementById('work-order-form-boat-length').value = ''
      document.getElementById('work-order-form-boat-beam').value = ''
      document.getElementById('work-order-form-boat-combo').value = ''
      document.getElementById('work-order-form-boat-mooring-tag').value = ''
      document.getElementById('work-order-form-boat-bottom-color').value = ''
      document.getElementById('work-order-form-boat-bottom-paint').value = ''
      document.getElementById('work-order-form-boat-mast-length').value = ''
      document.getElementById('work-order-form-boat-engine-count').value = ''
      document.getElementById('work-order-form-boat-battery-count').value = ''
      document.getElementById('work-order-form-boat-engine-type').value = ''
      document.getElementById('work-order-form-boat-boat-type').value = ''
      document.getElementById('work-order-form-boat-fuel-type').value = ''
      document.getElementById('work-order-form-boat-fuel-capacity').value = ''
      document.getElementById('shortDesc').value = ""

      var Parent = document.getElementById('activeJobs')
      while (Parent.hasChildNodes()) {
        Parent.removeChild(Parent.firstChild)
      }

      document.getElementById('add-ops-btn').classList.add('hidden')
      document.getElementById('add-btn').classList.remove('hidden')

      // Scoped to #activeJobs — see the matching fix in addOpen()'s/closeAdd()'s
      // close handlers for why an unscoped getElementsByClassName('active')
      // crashes here (picks up whichever unrelated element on the page happens
      // to be "active" and reads a garbage number off its id).
      const activeJobs = document.getElementById('activeJobs').getElementsByClassName('active')

      while (activeJobs.length) {

        const number = activeJobs[0].id.at(-1)

        document.getElementById("category" + number).value = ""
        document.getElementById("opType" + number).value = ""
        document.getElementById('opCode' + number).value = ""
        document.getElementById("description" + number).value = ""
        document.getElementById("date" + number).value = ""
        document.getElementById("tech" + number).value = ""
        document.getElementById("status" + number).value = ""
        document.getElementById("job" + number).classList.remove('active')
        document.getElementById("job" + number).classList.add('inactive')
        document.getElementById('addJobButton' + number).classList.remove('hidden')
        document.getElementById('deleteJobButton' + number).classList.add('hidden')
        document.getElementById('jobExit' + number).classList.remove('hidden')
        document.getElementById('jobMinimize' + number).classList.add('hidden')

      }

      var Parent = document.getElementById('activeJobs')
      while (Parent.hasChildNodes()) {
        Parent.removeChild(Parent.firstChild)
      }

      document.getElementById('generalWorkOrder').scrollIntoView();
      document.getElementById('Add').style.display = 'none';

      const storage = document.getElementById('winter-storage-section')
  const winterSpring = document.getElementById('storage-work-order-section')
  const dates = document.getElementById('work-order-date-section')
  const general = document.getElementById('general-work-order-section')
  

    storage.classList.add('hidden-form-section');
    dates.classList.add('hidden-form-section');
    winterSpring.classList.add('hidden-form-section')
    winterSpring.style.display='none';
    general.classList.remove('hidden-form-section');
    general.style.display='flex';

    const hiddenCategories = document.querySelectorAll('.hide-storage')
    hiddenCategories.forEach(category => {
      category.style.display = 'block';
    });
      
    
  });

  // Complete and close add button
  document.getElementById('add-ops-btn').addEventListener('click', closeAdd);

  // Add job button
  document.getElementById('addJobButton').addEventListener('click', function () {
    document.querySelector('.jobForm').scrollTop = 0;
    if (isEstimateMode() && !editingOperationJobBox) {
      addEstimateOperation();
    } else if (editingOperationJobBox) {
      saveEditedOperation(editingOperationJobBox);
    } else {
      setPendingOpSchedule(jobAssist.selection());
      addJob().finally(() => { setPendingOpSchedule(null); jobAssist.reset(); });
    }
  });

  // Delete job button
  document.getElementById('deleteJobButton').addEventListener('click', function () {
    deleteJob(this);
  });

  document.getElementById('mobile-menu-button').addEventListener('click', () => {
    document.querySelector('.page-select').classList.toggle('hide-mobile');
  })

  // part order button
  document.getElementById('add-part-order').addEventListener('click', () => {
    addPartOrder();
  });

  document.getElementById('open-part-order').addEventListener('click', () => {
    partBox()
  });

  // Clear part order button
  document.getElementById('clear-part-btn').addEventListener('click', clearPartOrder);

  // Add new customer button
  document.getElementById('add-customer-btn').addEventListener('click', addNewCustomer);

  // Job complete button — confirm step wraps submitOpen(), same handler underneath
  document.querySelector('.job-complete-button').addEventListener('click', () => {
    openJobCompleteConfirm();
  });

  document.getElementById('entryButton').addEventListener('click', showStatus);

  document.getElementById('holdButton').addEventListener('click', holdOperation);

  document.getElementById('deleteButton').addEventListener('click', deleteOperation)

  document.getElementById('entryButtonMobile').addEventListener('click', showStatus);

  document.getElementById('holdButtonMobile').addEventListener('click', holdOperation);

  document.getElementById('deleteButtonMobile').addEventListener('click', deleteOperation)

  document.getElementById('partBoxButtonMobile').addEventListener('click', partBox)

  document.getElementById('reports-view-button').addEventListener('click', function (event) {
    event.stopPropagation();
    toggleView('reports-view', this, event);
  });

  document.getElementById('filter-view-button').addEventListener('click', function (event) {
    event.stopPropagation();
    toggleView('filter-view', this, event);
  });



  document.addEventListener('click', function (event) {
    if(event.target.closest('button')?.id === 'log-out-button-no-role' || event.target.closest('button')?.id === 'log-out-button' || event.target.closest('button')?.id === 'reload-button-no-role'){
      return;
    }
    closeOpenViews(event.target);

    const updateDiv = document.getElementById('Update');
    const openSlidePanel = updateDiv.classList.contains('active');

    if (openSlidePanel &&
        !updateDiv.contains(event.target) &&
        !event.target.closest('[class*="slide-panel-"]') &&
        !event.target.closest('#partsModal') &&
        !event.target.closest('#laborAndPartsQS') &&
        !event.target.closest('#kit-select-modal') &&
        !event.target.closest('#kit-parts-modal') &&
        !event.target.closest('.part-popup') &&
        !event.target.closest('#shrinkwrap-menu') &&
        !event.target.closest('#billing-modal') &&
        !event.target.closest('#photoModal') &&
        // opPanel.js's dialogs/popovers (Hold, Decline, Move, the status-
        // strip "More" and overflow "More ⋯" menus) are appended to
        // document.body, not nested inside #Update, for correct stacking —
        // without this exemption every click inside them reads as "outside
        // #Update" and silently closes the panel underneath. Masked in
        // most flows because a successful submit re-opens #Update via
        // opCheck() right after, but a real bug (e.g. Cancel, or any
        // in-dialog click before submitting, leaves #Update closed
        // invisibly behind the dialog).
        !event.target.closest('.op-dialog-scrim') &&
        !event.target.closest('.op-popover') &&
        // Duplicate operation (opPanel.js, Stage 10) opens #Add on top of
        // #Update, which stays open behind it (rail context, sibling list)
        // so the panel is still there when the user finishes/cancels the
        // add-op form — same exemption reasoning as the two entries above.
        !event.target.closest('#Add') ){

      event.preventDefault();
      
      updateDiv.classList.remove('active');
      document.querySelectorAll('.slide-panel-static').forEach(panel => {
        panel.style.display = 'none';
      });

      document.querySelectorAll('.slide-panel-static-sub').forEach(panel => {
        panel.style.display = 'none';
      });

      if(document.getElementById('partsModal').style.display === 'flex'){
        document.getElementById('partsModal').style.display = 'none';
      }
    }
});


  function toggleView(viewId, button,) {
    var view = document.getElementById(viewId);

    // Toggle classes using toggle method
    view.classList.toggle('hideBox');
    view.classList.toggle('showBox');

    // Update position and size if shown
    if (view.classList.contains('showBox')) {
      var buttonRect = button.getBoundingClientRect();

      // Get the computed style of the view
      var viewStyle = window.getComputedStyle(view);
      var viewWidth = viewStyle.width;

      // Add transition to button
      button.style.transition = 'width 0.3s ease-in-out';
      button.style.backgroundColor = 'var(--harbor-navy)';
      button.style.color = 'white';

      // Set the button width to match the view (not on a phone, where the box spans the screen)
      if (!window.matchMedia('(max-width: 760px)').matches) {
        setTimeout(() => {
          button.style.width = viewWidth;
        }, 0);
      }

      // Make the view visible
      view.style.visibility = 'visible';
    } else {
      // Animate button back to original width
      button.style.transition = 'width 0.3s ease-in-out';
      button.style.width = '';
      button.style.backgroundColor = '';
      button.style.color = '';
      view.style.visibility = 'hidden';
    }
  }

  document.querySelector('.page-buttons').addEventListener('click', () => {
    const menuButton = document.getElementById('mobile-menu-button');
    if (!menuButton.classList.contains('hide-mobile')) {
      menuButton.click();
      menuButton.querySelector('svg').classList.toggle('toggle');
    }
  });
  

    document.getElementById('open-work-button').addEventListener('click', () => {
    const elements = document.querySelectorAll('.page-view')
    elements.forEach(page => {
      page.style.display = 'none';
      page.style.visibility = 'hidden';
    });
    document.getElementById('open-work').style.display = 'flex';
    document.getElementById('open-work').style.visibility = 'visible';
    document.getElementById('work-order-view').style.display = 'flex';
    document.getElementById('current-page').innerHTML = "Work Orders";
    switchView(currentJobView);
  });


document.getElementById('map-open-button').addEventListener('click', async () => {
  await showPage('maps'); // shows the maps section/container
  const iframe = ensureMapsIframe();
  if (!iframe) {
    console.error('[app.js] #maps host not found');
    return;
  }

  // If it’s already loaded (from a prior click), post immediately
  if (iframe.dataset.loaded || iframe.contentDocument?.readyState === 'complete') {
    iframe.dataset.loaded = '1';
    try {
      iframe.contentWindow.postMessage({ action: 'renderMaps' }, '*');
      console.log('[app.js] maps iframe already loaded → renderMaps posted');
    } catch (e) {
      console.error('[app.js] postMessage (already loaded) failed', e);
    }
  }
});



document.getElementById('dashboard-open-button')
        .addEventListener('click', () => {
          document.getElementById('current-page').textContent = 'Dashboard';
          showPage('dashboard');
        });

document.getElementById('customers-open-button')
        .addEventListener('click', () => showPage('customers'));

document.getElementById('scheduler-open-button')
        .addEventListener('click', () => showPage('scheduler'));

document.getElementById('maintenance-open-button')
        ?.addEventListener('click', () => showPage('maintenance'));

document.getElementById('compliance-open-button')
        ?.addEventListener('click', () => showPage('compliance'));

// Help opens at the chapter for the page that was on screen (help.js maps the page-view id).
document.getElementById('help-open-button')
        ?.addEventListener('click', async () => {
          const from = [...document.querySelectorAll('.page-view')]
            .find(el => el.id !== 'help' && el.style.display === 'flex');
          await showPage('help');
          document.getElementById('current-page').textContent = 'Help';
          const w = document.getElementById('help')?.querySelector('iframe')?.contentWindow;
          try { if (w && from) w.location.hash = `page=${from.id}`; } catch { /* other origin — ignore */ }
        });
document.getElementById('battery-open-button')
        ?.addEventListener('click', () => showPage('battery'));
document.getElementById('equipment-open-button')
        ?.addEventListener('click', () => showPage('equipment'));
document.getElementById('slips-open-button')
        ?.addEventListener('click', () => showPage('slips'));
// The launch crew's own app (fuel, launch checks, drills, clock) — its own page, so a new tab.
document.getElementById('dock-schedule-open-button')
        ?.addEventListener('click', () => showPage('dockSchedule'));
document.getElementById('uscg-open-button')
        ?.addEventListener('click', () => showPage('uscg'));
document.getElementById('dock-app-rail-button')
        ?.addEventListener('click', () => window.open('/dock.html', '_blank', 'noopener'));
document.getElementById('timeclock-open-button')
        ?.addEventListener('click', () => showPage('timeclock'));
document.getElementById('requests-open-button')
        ?.addEventListener('click', () => showPage('requests'));
document.getElementById('reports-open-button')
        ?.addEventListener('click', () => showPage('reports'));
document.getElementById('drystack-open-button')
        ?.addEventListener('click', () => showPage('drystack'));

document.getElementById('inventory-menu-open-button')
        .addEventListener('click', () => showPage('inventoryDatabase'));

document.getElementById('settings-button')
        .addEventListener('click', () => showPage('settings'));



});

// ---- Rail hubs (railHubs.js, design_handoff_rail_fold) ----
// The pages the Moorings and Fuel flyouts used to list are off-stage rail buttons now (index.html
// #rail-hub-members), opened by their hub's tabs or, if the user gave one its own link, from the rail.
Object.values(HUBS).forEach(hub => hub.tabs.filter(t => t.wire).forEach(tab => {
  document.getElementById(tab.key)?.addEventListener('click', async () => {
    await showPage(tab.page);
    document.getElementById('current-page').textContent = tab.title || tab.label;
    // A tab can open a page on one of its modes (Swing check → Mooring Field #swing).
    if (tab.hash) {
      const w = document.getElementById(tab.page)?.querySelector('iframe')?.contentWindow;
      try { if (w) w.location.hash = tab.hash; } catch { /* other origin — ignore */ }
    }
  });
}));
// Launch Dispatch's and Mooring Field's action counts (waiting pickups, positions to review) are
// kept by shellNav.js; their hubs add them up (railHubs.js).

/* ---- Create: the rail's New button and menu, and "new …" in search ---- */

// The New Work Order form makes all three; the type is preselected.
const NEW_FORM_TYPES = { wo: 'general-work-order', estimate: 'estimate', storage: 'storage-work-order' };

/**
 * Create something. kind: wo | estimate | storage | customer | invoice | email. For the three form
 * types, ctx { customerId, boatId } opens the form with that customer and boat already chosen
 * (the New menu's "For this customer", a search result's + WO / + Est, the Requests page).
 */
async function createNew(kind, ctx = {}) {
  closeNewMenu();
  if (NEW_FORM_TYPES[kind]) {
    addOpen(true);
    document.querySelector(`.wo-type-btn[data-wo-type="${NEW_FORM_TYPES[kind]}"]`)?.click();
    if (ctx.customerId) await chooseFormCustomer(ctx.customerId, ctx.boatId);
    return;
  }
  if (kind === 'customer') {
    selectRailItem('customers-open-button');
    await showPage('customers');
    document.getElementById('current-page').textContent = 'Customers';
    postToPageFrame('customers', { type: 'newCustomer' });
    return;
  }
  if (kind === 'invoice') { openInvoiceEditor({ draft: { lines: [] } }); return; }
  if (kind === 'email') document.getElementById('email-open-button')?.click();
}

/** Fill the New Work Order form's customer and boat (fresh from the database, the device copy offline). */
async function chooseFormCustomer(customerId, boatId) {
  let customer = null;
  try { customer = (await get(ref(database, `customers/${customerId}`))).val(); } catch { /* offline */ }
  if (!customer) customer = await idbGet('customerData', customerId).catch(() => null);
  if (!customer) { toastMessage('That customer no longer exists.', 'red'); return; }
  const boat = (boatId && customer.Boats?.[boatId]) || null;
  selectCustomerAndBoat({ ...customer, Customer_ID: customer.Customer_ID || customerId }, boat ? { ...boat, Boat_ID: boat.Boat_ID || boatId } : null);
}

/** The customer (and boat) open on the Customers page right now, if any — the New menu offers to start from it. */
async function openCustomerContext() {
  if (document.getElementById('customers')?.style.display !== 'flex') return null;
  let doc = null;
  try { doc = document.querySelector('#customers iframe')?.contentDocument; } catch { return null; }
  const root = doc?.getElementById('cs-record-root');
  if (!root || root.classList.contains('cs-hidden')) return null;
  const customerId = doc.getElementById('currentCustomerId')?.value;
  if (!customerId) return null;
  const customer = await idbGet('customerData', customerId).catch(() => null);
  if (!customer) return null;
  const boatId = doc.getElementById('currentBoatId')?.value || '';
  const boat = boatId ? customer.Boats?.[boatId] : null;
  const name = [customer.First_Name, customer.Last_Name].filter(Boolean).join(' ') || customer.Company || 'this customer';
  return { customerId, boatId: boat ? boatId : '', label: boat?.Boat_Name ? `${name} · ${boat.Boat_Name}` : name };
}

/** Can this person make invoices? (Same switch the Invoices list button follows.) */
const canInvoice = () => { const b = document.getElementById('invoices-list-button'); return !!b && !b.classList.contains('inv-hidden'); };

let newMenuContext = null;
async function openNewMenu() {
  const menu = document.getElementById('railNewMenu');
  if (!menu || !menu.hidden) return;
  menu.querySelector('[data-new="invoice"]').hidden = !canInvoice();
  // A customer is open on the Customers page: offer to start from them first.
  newMenuContext = await openCustomerContext();
  const ctxBox = document.getElementById('railNewContext');
  if (ctxBox) {
    ctxBox.hidden = !newMenuContext;
    if (newMenuContext) ctxBox.querySelector('.rnm-ctx-name').textContent = newMenuContext.label;
  }
  menu.hidden = false;
  document.getElementById('rail-new-more').setAttribute('aria-expanded', 'true');
  [...menu.querySelectorAll('.rnm-row')].find(r => r.offsetParent !== null)?.focus();
}

function closeNewMenu({ focus = false } = {}) {
  const menu = document.getElementById('railNewMenu');
  if (!menu || menu.hidden) return;
  menu.hidden = true;
  const more = document.getElementById('rail-new-more');
  more?.setAttribute('aria-expanded', 'false');
  if (focus) more?.focus();
}

function wireNewMenu() {
  const main = document.getElementById('rail-new-wo');
  const more = document.getElementById('rail-new-more');
  const menu = document.getElementById('railNewMenu');
  if (!main || !more || !menu) return;
  // On the slim icon rail there is room for "+" only: it opens the menu.
  main.addEventListener('click', (e) => {
    e.stopPropagation();
    if (document.body.classList.contains('rail-style-icons')) { if (menu.hidden) openNewMenu(); else closeNewMenu(); return; }
    createNew('wo');
  });
  more.addEventListener('click', (e) => { e.stopPropagation(); if (menu.hidden) openNewMenu(); else closeNewMenu(); });
  menu.addEventListener('click', (e) => {
    const row = e.target.closest('[data-new]');
    if (!row) return;
    const ctx = row.dataset.ctx && newMenuContext ? { customerId: newMenuContext.customerId, boatId: newMenuContext.boatId } : {};
    createNew(row.dataset.new, ctx);
  });
  menu.addEventListener('keydown', (e) => {
    const rows = [...menu.querySelectorAll('.rnm-row')].filter(r => r.offsetParent !== null);
    const i = rows.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      rows[(i + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length]?.focus();
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) closeNewMenu({ focus: true }); });
  document.addEventListener('click', (e) => { if (!menu.hidden && !e.target.closest('#railNew')) closeNewMenu(); });
}

function selectRailItem(id) {
  document.querySelectorAll('.selected-tide-button').forEach(b => b.classList.remove('selected-tide-button'));
  document.getElementById(id)?.classList.add('selected-tide-button');
}

/** Posts to a page iframe once it has loaded (a brand-new iframe may still be loading). */
function postToPageFrame(pageId, msg) {
  const iframe = document.getElementById(pageId)?.querySelector('iframe');
  if (!iframe) return;
  const post = () => iframe.contentWindow?.postMessage(msg, window.location.origin);
  let loaded = false;
  try { loaded = iframe.contentDocument?.readyState === 'complete' && iframe.contentWindow.location.href !== 'about:blank'; } catch { loaded = false; }
  if (loaded) post(); else iframe.addEventListener('load', post, { once: true });
}

// Global search in the rail (shellNav.js) picks a record; the shell opens it.
function startShellNav() {
  initRailHubs();
  initShellKeys({ createNew, openAllPages: () => openLauncher() });
  initMobileBar({ createNew, customerContext: openCustomerContext, canInvoice });
  initRailLayout();
  initInvoices();
  initEvents();
  // Settings › Features: hide what this marina switched off, live.
  watchFeatures(() => refreshRail());
  initShellNav({
    createNew,
    canInvoice,
    openEstimate: async (id) => { await openEstimateById(id); },
    openEstimates: () => openEstimatesList(),
    syncEstimates: () => syncEstimatesToDevice(),
    openInvoices: (search) => openInvoicesList('invoices', search),
    // Staff, for admin and office (they open in Settings › Team; others can't see the roster there).
    listStaff: () => (canInvoice() ? Object.entries(globalUserData || {})
      .filter(([id, u]) => id !== 'lastUpdated' && u && typeof u === 'object' && (u.displayName || u.name))
      .map(([id, u]) => ({ id, name: u.displayName || u.name, role: String(u.role || '') })) : []),
    // Settings on one of its tabs (e.g. the rail's "Email needs reconnecting").
    openSettings: async (tab) => {
      document.getElementById('settings').innerHTML = `<iframe src="/settings.html?tab=${encodeURIComponent(tab)}" class="page-frame"></iframe>`;
      await showPage('settings');
    },
    openWorkOrder: async (wo) => {
      selectRailItem('open-work-button');
      await applyOpenWorkDashboardNav({ workOrder: wo });
      // Not in the open list — it's closed: show it in Closed, searched.
      if (!document.querySelector(`.table-item[data-workorder="${CSS.escape(String(wo))}"]`)) {
        await applyOpenWorkDashboardNav({ view: 'closed' });
        const box = document.getElementById('closed-search');
        if (box) { box.value = String(wo); box.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true })); }
        toastMessage(`WO ${wo} isn't open — showing it in Closed`, '#3D4F60', 3000);
      }
    },
    openCustomer: async (customerId) => {
      selectRailItem('customers-open-button');
      await showPage('customers');
      document.getElementById('current-page').textContent = 'Customers';
      postToPageFrame('customers', { type: 'customerLookup', customerId, return: null });
    },
    openMooring: async (tag) => {
      await showPage('mooringDatabase');
      document.getElementById('current-page').textContent = 'Mooring Database';
      postToPageFrame('mooringDatabase', { type: 'dashboardFilter', filter: { tag } });
    },
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startShellNav, { once: true });
else startShellNav();

function isMobile() {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function focusIfNotMobile(element) {
  if (!isMobile()) {
    element.focus();
  }
}

async function switchView(view) {

  currentJobView = view;

  const workOrderView = document.getElementById('work-order-view');
  const workOrderSearch = document.getElementById('search');
  const jobView = document.getElementById('job-view');
  const jobSearch = document.getElementById('job-search');
  const currentPage = document.getElementById('current-page');
  const closedView = document.getElementById('closed-view');
  const closedSearch = document.getElementById('closed-search');
  const contractsView = document.getElementById('contracts-view');
  const contractsSearch = document.getElementById('contracts-search');
  const shrinkwrapView = document.getElementById('shrinkwrap-view');
  const shrinkwrapSearch = document.getElementById('shrinkwrap-search');
  const canvasView = document.getElementById('canvas-view');
  const canvasSearch = document.getElementById('canvas-search');
  const internalView = document.getElementById('internal-view');
  const internalSearch = document.getElementById('internal-search');

  closeOpenViews();

  document.querySelectorAll('.wo-view-tab').forEach(tab => tab.classList.remove('active'));
  const activeTabId = {
    workOrder: 'switch-work-order-view', job: 'switch-job-view',
    closed: 'switch-closed-view', contracts: 'switch-contracts-view',
    shrinkwrap: 'switch-shrinkwrap-view', canvas: 'switch-canvas-view', internal: 'switch-internal-view',
  }[view];
  document.getElementById(activeTabId)?.classList.add('active');

  const elements = document.querySelectorAll('.page-view');
  elements.forEach(page => {
    page.style.display = 'none';
    page.style.visibility = 'hidden';
  });

  document.getElementById('open-work').style.display = 'flex';
  document.getElementById('open-work').style.visibility = 'visible';

  // Hide all views and searches. closedView/contractsView were never in this
  // list (only the original 4 sub-views were) — added them too so switching away
  // from Closed/Storage Contracts actually hides them instead of leaving them
  // stacked under whatever's shown next. Daily Log isn't one of these views: it
  // has its own page (#daily-log-div), hidden with the other .page-view pages above.
  [workOrderView, jobView, closedView, contractsView, shrinkwrapView, canvasView, internalView].forEach(v => v && (v.style.display = 'none'));
  [workOrderSearch, jobSearch, closedSearch, contractsSearch, shrinkwrapSearch, canvasSearch, internalSearch].forEach(s => s && s.classList.add('hidden'));
  const woQuickFiltersEl = document.getElementById('wo-quick-filters');
  const jobQuickFiltersEl = document.getElementById('job-quick-filters');
  if (woQuickFiltersEl) woQuickFiltersEl.style.display = 'none';
  if (jobQuickFiltersEl) jobQuickFiltersEl.style.display = 'none';

  // Show the selected view and search
  switch (view) {
    case 'job':
      jobView.style.display = 'flex';
      jobSearch.classList.remove('hidden');
      currentPage.innerHTML = "Operations";
      document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
      document.getElementById('print-job-table').style.display = 'flex';
      focusIfNotMobile(jobSearch);
      document.getElementById('wo-quick-filters').style.display = 'none';
      document.getElementById('job-quick-filters').style.display = 'flex';
      updateJobQuickFilterCounts();
      break;
    case 'workOrder':
      workOrderView.style.display = 'flex';
      workOrderSearch.classList.remove('hidden');
      currentPage.innerHTML = "Work Orders";
      document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
      focusIfNotMobile(workOrderSearch);
      document.getElementById('wo-quick-filters').style.display = 'flex';
      document.getElementById('job-quick-filters').style.display = 'none';
      break;
      case 'closed':
        // Text left in the box from last time still applies.
        if (closedSearch.value.trim()) await searchClosedJobsIndexedDB(); else await closedJobList();
        closedView.style.display = 'flex';
        closedSearch.classList.remove('hidden');
        currentPage.innerHTML = "Closed Operations";
        document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
        document.getElementById('print-job-table').style.display = 'flex';
        focusIfNotMobile(closedSearch);
        break;
        case 'contracts':
          await renderStorageContractsList();
          contractsView.style.display = 'flex';
          contractsSearch.classList.remove('hidden');
          currentPage.innerHTML = "Storage Contracts";
          document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
          focusIfNotMobile(contractsSearch);
          break;
        case 'shrinkwrap':
          shrinkwrapView.style.display = 'flex';
          await renderShrinkwrapView();
          shrinkwrapSearch.classList.remove('hidden');
          currentPage.innerHTML = "Shrinkwrap";
          document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
          focusIfNotMobile(shrinkwrapSearch);
          break;
        case 'canvas':
          canvasView.style.display = 'flex';
          await renderCanvasView();
          canvasSearch.classList.remove('hidden');
          currentPage.innerHTML = "Canvas &amp; sails";
          document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
          focusIfNotMobile(canvasSearch);
          break;
        case 'internal':
          internalView.style.display = 'flex';
          await renderInternalView();
          internalSearch.classList.remove('hidden');
          currentPage.innerHTML = "Internal work";
          document.querySelectorAll('.print-table-button').forEach(button => button.style.display = 'none');
          focusIfNotMobile(internalSearch);
          break;
  }
}

document.getElementById('switch-job-view').addEventListener('click', () => switchView('job'));
document.getElementById('switch-work-order-view').addEventListener('click', () => switchView('workOrder'));
document.getElementById('switch-closed-view').addEventListener('click', () => switchView('closed'));
document.getElementById('switch-contracts-view').addEventListener('click', () => switchView('contracts'));
document.getElementById('switch-shrinkwrap-view').addEventListener('click', () => switchView('shrinkwrap'));
document.getElementById('switch-canvas-view').addEventListener('click', () => switchView('canvas'));
document.getElementById('switch-internal-view').addEventListener('click', () => switchView('internal'));

document.getElementById('haul-launch-open-button').addEventListener('click', function() {
  document.getElementById('current-page').textContent = 'Haul / Launch';
  showPage('haulLaunch');
});

document.getElementById('settings-button').addEventListener('click', () => {
  const elements = document.querySelectorAll('.page-view')
  elements.forEach(page => {
    page.style.display = 'none';
    page.style.visibility = 'hidden';
  });
  document.getElementById('settings').style.display = 'flex';
  document.getElementById('settings').style.visibility = 'visible';
  document.getElementById('current-page').innerHTML = "Settings";
});



function closeOpenViews(exceptElement) {

  if (exceptElement && exceptElement.closest('div')?.classList.contains('filter-box')) {
    return;
  }

  var openViews = document.querySelectorAll('.showBox');
  openViews.forEach(function (view) {
    if (view !== exceptElement && !view.contains(exceptElement)) {
      view.classList.remove('showBox');
      view.classList.add('hideBox');
      view.style.visibility = 'hidden';

      // Reset the associated button's width
      var buttonId = view.id.replace('-view', '-view-button');
      var button = document.getElementById(buttonId);
      if (button) {
        button.style.transition = 'width 0.3s ease-in-out';
        button.style.width = '';
        button.style.backgroundColor = '';
        button.style.color = 'black';
      }
    }
  });
}

// Resolves once Firebase Auth actually has a signed-in user. RTDB rules
// require auth != null for role-gated paths like /pricing below — reads
// fired from DOMContentLoaded or bare module scope can race ahead of auth
// resolving (it restores asynchronously), hitting the database while
// auth.currentUser is still null and getting a spurious Permission denied.
document.addEventListener('DOMContentLoaded', async () => {

  if (!navigator.onLine) {
    console.log('User is offline. Skipping function.');
    return;
  }

  try {
    await waitForAuthUser();
    const hourlyRateRef = ref(database, '/pricing/Hourly Rate');
    const snapshot = await get(hourlyRateRef);
    const hourlyRate = snapshot.val();
    document.getElementById('currentLaborRate').value = hourlyRate;
  } catch (error) {
    console.error("Error fetching hourly rate: ", error);
  }
});


document.addEventListener('DOMContentLoaded', () => {
  var elements = document.querySelectorAll('.engineW')
  elements.forEach((element) => {
    element.addEventListener('click', () => {
      document.getElementById('bayTestWinter').checked = element.checked
    })
  })
})

document.addEventListener('DOMContentLoaded', () => {
  var elements = document.querySelectorAll('.engineS')
  elements.forEach((element) => {
    element.addEventListener('click', () => {
      document.getElementById('bayTestSpring').checked = element.checked
    })
  })
})

document.addEventListener('DOMContentLoaded', async function () {
  var date = new Date().toLocaleDateString('en-us', { year: "numeric", month: "2-digit", day: "2-digit", timeZone: 'UTC' });
  var year = date.split('/')[2];
  var nextYear = (Number(year) + 1);
  document.getElementById('work-order-form-dates-launch-date').value = nextYear + "-04-01";
});

initTidePanel();

async function holdOperation() {
  const jobID = document.getElementById('currentJobId').value;
  try {
    const dbRef = ref(database, `/data/${jobID}`);
    await update(dbRef, { "Status": "Hold" });
    await updateJobDB(jobID, 'Status', "Hold");
    mainTable();
  } catch (error) {
    console.error('Failed to put job on hold', error);
  };
};

function partBox() {

  document.getElementById('partsModal').style.display = 'flex'
  document.getElementById('partId').value = '';
  document.getElementById('partVendorList').value = '';
  document.getElementById('vendor').value = '';
  document.getElementById('manufacturer').value = '';

  var tbody = document.getElementById("op-body")

  tbody.onclick = function (e) {

    e = e || window.event;
    var data = [];
    var target = e.srcElement || e.target;
    while (target && target.nodeName !== "TR") {
      target = target.parentNode;
    }
    if (target) {
      var cells = target.getElementsByTagName("td");
      for (var i = 0; i < cells.length; i++) {
        data.push(cells[i].innerHTML);
      }
    }

    document.getElementById('partWorkOrder').value = data[6]
    document.getElementById('partJobId').value = data[0]

  }
}

async function getAllCustomerData() {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction(['customerData'], 'readonly');
      const store = transaction.objectStore('customerData');
      const allCustomers = [];

      const cursorRequest = store.openCursor();

      cursorRequest.onerror = (event) => {
        console.error('Error fetching customer data:', event.target.errorCode);
        reject('Error fetching customer data: ' + event.target.errorCode);
      };

      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          allCustomers.push(cursor.value);
          cursor.continue();
        } else {
          resolve(allCustomers);
        }
      };
    } catch (error) {
      console.error('Database error:', error);
      reject('Database error: ' + (error?.message || error));
    }
  });
}



async function getCustomerDataById(customerId, boatId, data) {
  return new Promise(async (resolve, reject) => {
    try {
      // Check if customerId is valid (do this before touching IndexedDB)
      if (!customerId) {
        console.warn('Invalid customerId:', customerId);
        reject('Invalid customerId provided');
        return;
      }

      const db = await getDB();
      const transaction = db.transaction(['customerData'], 'readonly');
      const store = transaction.objectStore('customerData');

      const customerData = await new Promise((res, rej) => {
        const req = store.get(customerId);

        req.onerror = (event) => {
          console.error('Error fetching customer data:', event.target.errorCode);
          console.warn('Problematic customerId:', customerId);
          rej('Error fetching customer data: ' + event.target.errorCode);
        };

        req.onsuccess = () => res(req.result || null);
      });

      if (customerData) {
        if (boatId && customerData.Boats && customerData.Boats[boatId]) {
          resolve({ customerData: customerData, boatData: customerData.Boats[boatId] });
        } else if (boatId) {
          console.warn('Boat data not found for customerId:', customerId, 'boatId:', boatId);
          resolve({ customerData: customerData, boatData: null });
        } else {
          resolve({ customerData: customerData });
        }
      } else {
        console.warn('Customer data not found for customerId:', customerId);
        resolve(null);
      }
    } catch (error) {
      console.error('Database error:', error);
      reject('Database error: ' + (error?.message || error));
    }
  });
}


/* async function checkAndUpdateDMToken() {
  const apiRef = child(ref(database), 'API');
  const snapshot = await get(apiRef);
  const dmConfig = snapshot.val();

  const twoDaysInMillis = 2 * 24 * 60 * 60 * 1000;
  const lastUpdated = dmConfig.lastUpdated || 0;

  if (Date.now() - lastUpdated > twoDaysInMillis) {
    const functions = getFunctions();
    const updateToken = httpsCallable(functions, 'getDMTokenAndUpdateManual');
    
    try {
      await updateToken();
    } catch (error) {
      console.error('Error updating token:', error);
      throw error;
    }

    const updatedSnapshot = await get(apiRef);
    return updatedSnapshot.val().DockmasterDMToken;
  }

  return dmConfig.DockmasterDMToken;
} */

/* async function getCustomerBalance(customer) {

  const DOCKMASTER_API_KEY = await checkAndUpdateDMToken();
  const DM_URL = 'https://api.dockmaster.com:4134/';

  const url = `${DM_URL}api/v2/Customers/RetrieveCustomer/${customer}`;
  const options = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${DOCKMASTER_API_KEY}`,
      'Content-Type': 'application/json'
    }
  };

  try {
    const response = await fetch(url, options);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if(!result.Invoices){
      return null;
    }

    if (result.Invoices) {
      const rows = [];

      for (const inv of result.Invoices) {

        if(!inv.Description.includes('WO Billing')){
          continue;
        }

        const tbody = document.getElementById('invoice-body');
        tbody.innerHTML = '';

        if (inv.Description.split(' ')[0] !== '') {
          const workOrderCheck = inv.Description.split(' ')[0];
          const workOrderData = await getWorkOrderInfoDM(workOrderCheck, inv.DueDate, inv.Id);

          console.log(workOrderData);

          if(workOrderData.length === 0) {
            return
          }

          for (const r of workOrderData) {
            const row = document.createElement('tr');
            const invoiceNumber = document.createElement('td');
            invoiceNumber.textContent = inv.Id;
            const workOrder = document.createElement('td');
            workOrder.innerHTML = r.Work_Order;
            const description = document.createElement('td');
            description.textContent = r.Description.split(':')[0];
            description.style.fontSize = '.75rem';
            const date = document.createElement('td');
            date.innerHTML = r.Bill_Date;

            const startDate = parseDay(r.Bill_Date);
            const endDate = new Date();
            const differenceInMilliseconds = endDate - startDate;
            const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

            const pastDue = document.createElement('td');
            pastDue.innerHTML = differenceInDays;
            const amount = document.createElement('td');
            amount.innerHTML = r.Charge;

            row.appendChild(invoiceNumber);
            row.appendChild(workOrder);
            row.appendChild(description);
            row.appendChild(date);
            row.appendChild(pastDue);
            row.appendChild(amount);

            rows.push(row);
          }
        } else {
          const row = document.createElement('tr');
          const invoiceNumber = document.createElement('td');
          invoiceNumber.textContent = inv.Id;
          const workOrder = document.createElement('td');
          workOrder.innerHTML = "";
          const description = document.createElement('td');
          description.innerHTML = 'Point of Sale';
          const date = document.createElement('td');
          date.innerHTML = inv.DueDate;

          const startDate = parseDay(inv.DueDate);
          const endDate = new Date();
          const differenceInMilliseconds = endDate - startDate;
          const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

          const pastDue = document.createElement('td');
          pastDue.innerHTML = differenceInDays;
          const amount = document.createElement('td');
          amount.innerHTML = `$${Number(inv.InvoiceAmount).toFixed(2)}`;

          row.appendChild(invoiceNumber);
          row.appendChild(workOrder);
          row.appendChild(description);
          row.appendChild(date);
          row.appendChild(pastDue);
          row.appendChild(amount);

          rows.push(row);
        }
      }

      // Sort rows by date
      rows.sort((a, b) => {
        const dateA = new Date(a.querySelector('td:nth-child(4)').innerText);
        const dateB = new Date(b.querySelector('td:nth-child(4)').innerText);
        return dateA - dateB;
      });

      // Append sorted rows to the table
      const tbody = document.getElementById('invoice-body');
      rows.forEach(row => tbody.appendChild(row));
    

    let balance = result.Balance;
    let numStr = balance.toString();

    if (numStr.length < 3) {
      while (numStr.length < 3) {
        numStr = '0' + numStr;
      }
    }

    let formattedNumber = numStr.slice(0, -2) + '.' + numStr.slice(-2);
    return formattedNumber;
  }
  } catch (error) {
    console.error('Error fetching customer balance:', error);
    return null;
  }
} */

// Mutable (not const): applyBusinessBranding() overwrites the business-name/email
// portions once the configured Business Profile loads — address/phone stay as the
// literal fallback below since neither is part of that settings model yet.
let ESTIMATE_LETTERHEAD_ADDRESS = letterheadLine(cachedBusinessProfile);

document.getElementById('generalWorkOrder').addEventListener('input', () => {

})

async function fetchCustomerDataAndGenerateTable() {
  try {
    const db = await getDB();
    const transaction = db.transaction(['customerData'], 'readonly');
    const store = transaction.objectStore('customerData');

    const dataArray = await new Promise((resolve, reject) => {
      const req = store.getAll();

      req.onerror = (event) => {
        console.error('Error fetching customer data:', event.target.errorCode);
        reject(event.target.errorCode);
      };

      req.onsuccess = () => resolve(req.result || []);
    });

    generateCustomerTable(dataArray.filter(r => r.id !== 'lastUpdated'));

  } catch (error) {
    console.log(error);
  }
}


function generateCustomerTable(dataArray) {

  globalCustomerData = dataArray;

  const customerTables = ['tbody', 'customer-table-body']

  for (let table of customerTables) {
    var parent = document.getElementById(table);
    while (parent.hasChildNodes()) {
      parent.removeChild(parent.firstChild);
    }
  }

  dataArray = dataArray.sort((a, b) => {
    if (a.Last_Name && b.Last_Name) {
      return a.Last_Name.localeCompare(b.Last_Name);
    } else if (a.Last_Name) {
      return -1;
    } else if (b.Last_Name) {
      return 1;
    } else {
      return 0;
    }
  });

  dataArray.forEach(function (customer) {

    if(customer.Boats){
      Object.values(customer.Boats).forEach(boat => {

        const currentYear = new Date().getFullYear().toString();

        if(boat.Date_Hauled){

          if(boat.Date_Hauled.includes(currentYear)){
            haulStatus[boat.Boat_ID] = boat.Date_Hauled;
          }

        }
      });
    }

    var tbody = document.getElementById("tbody");
    var row = document.createElement("tr");

    let customerName;

    if(customer.Last_Name && customer.First_Name){
      customerName = customer.Last_Name + ", " + customer.First_Name;
    }else if(customer.Last_Name && customer.First_Name === ""){
      customerName = customer.Last_Name;
    }else if(customer.First_Name && customer.Last_Name === ""){
      customerName = customer.First_Name;
    }

    var nameTd = document.createElement("td");
    nameTd.className = 'form-data';
    nameTd.textContent = customerName;
    nameTd.setAttribute('data-customerInfo', JSON.stringify(customer))

    nameTd.addEventListener('click', async (e) => {
      const currentCustomerId = JSON.parse(e.target.getAttribute('data-customerInfo')).id
      const customerInfo = await getCustomerDataById(currentCustomerId);
      const customerData = customerInfo.customerData;
      if (customerData['Boats'] && Object.keys(customerData['Boats']).length > 1) {
        showBoatNamesPopup(customerData['Boats']);
      } else if(customerData['Boats'] && Object.keys(customerData['Boats']).length === 1){

        const boatKey = Object.keys(customerData['Boats'])[0];
        const boat = customerData['Boats'][boatKey];
        document.getElementById("work-order-form-boat-name").value = boat.Boat_Name
        document.getElementById("work-order-form-boat-id").value = boat.Boat_ID
        document.getElementById("work-order-form-boat-length").value = boat.Boat_Length
        document.getElementById("work-order-form-boat-beam").value = boat.Boat_Beam
        document.getElementById("work-order-form-boat-bottom-paint").value = boat.Bottom_Paint_Type
        document.getElementById("work-order-form-boat-bottom-color").value = boat.Bottom_Paint_Color
        document.getElementById('work-order-form-boat-mast-length').value = boat.Mast_Length
        document.getElementById('work-order-form-boat-boat-type').value = boat.Boat_Type;
        document.getElementById('work-order-form-boat-engine-count').value = boat.Number_of_Engines
        document.getElementById('work-order-form-boat-engine-type').value = boat.Engine_Type;
        document.getElementById('work-order-form-boat-battery-count').value = boat.Number_of_Batteries
        document.getElementById('work-order-form-boat-combo').value = boat.Combo_Key
        document.getElementById('work-order-form-boat-mooring-number-slip').value = boat.Mooring_Number
        document.getElementById('work-order-form-boat-mooring-tag').value = boat.Tag_Number
        document.getElementById('work-order-form-boat-mooring-weight').value = boat.Mooring_Weight
        document.getElementById('work-order-form-boat-DMid').value = boat.DM_Boat
      }

      storagePrice()
      document.getElementById('customer-search').value = ""
      getOpCodes()
      document.getElementById("customerData").style.display = "none";

      document.getElementById('Add').style.visibility = 'visible';

      document.getElementById("work-order-form-customer-id").value = customerData.Customer_ID
      document.getElementById("work-order-form-customer-name").value = customerName;
      document.getElementById('work-order-form-customer-DMid').value = customerData.DM_Customer
      document.getElementById('work-order-form-customer-phone').value = formatPhoneNumber(customerData.Phone);
      document.getElementById("work-order-form-customer-email").value = customerData.Email;
      document.getElementById("work-order-form-customer-street").value = customerData.Street;
      document.getElementById("work-order-form-customer-city").value = customerData.City;
      document.getElementById("work-order-form-customer-state").value = customerData.State;
      document.getElementById("work-order-form-customer-zip").value = customerData.Zip;
      resetSearch('myTable')
      syncWoContextStrip();
    });

    row.appendChild(nameTd);
    tbody.appendChild(row);
  });
  populateTable(dataArray)
}

function showBoatNamesPopup(boats) {

  var popup = document.getElementById('boat-popup-list')

  while (popup.hasChildNodes()) {
    popup.removeChild(popup.firstChild)
  };

  var boatList = document.createElement('ul');

  var boatArray = Object.values(boats)
  .filter(boat => typeof boat === 'object' && boat !== null)
  .sort((a, b) => (a.Boat_Name || "").localeCompare(b.Boat_Name || ""));


  boatArray.forEach(function (boat) {
    var listItem = document.createElement('li');
    listItem.textContent = boat['Boat_Name'];
    listItem.addEventListener("click", () => {

      document.getElementById("work-order-form-boat-name").value = boat.Boat_Name
      document.getElementById("work-order-form-boat-id").value = boat.Boat_ID
      document.getElementById("work-order-form-boat-length").value = boat.Boat_Length
      document.getElementById("work-order-form-boat-beam").value = boat.Boat_Beam
      document.getElementById("work-order-form-boat-bottom-paint").value = boat.Bottom_Paint_Type
      document.getElementById("work-order-form-boat-bottom-color").value = boat.Bottom_Paint_Color
      document.getElementById('work-order-form-boat-mast-length').value = boat.Mast_Length
      document.getElementById('work-order-form-boat-engine-count').value = boat.Number_of_Engines
      document.getElementById('work-order-form-boat-engine-type').value = boat.Engine_Type;
      console.log(boat.Engine_Type);
      document.getElementById('work-order-form-boat-fuel-type').value = boat.Fuel_Type;
      document.getElementById('work-order-form-boat-battery-count').value = boat.Number_of_Batteries
      document.getElementById('work-order-form-boat-combo').value = boat.Combo_Key
      document.getElementById('work-order-form-boat-mooring-number-slip').value = boat.Mooring_Number
      document.getElementById('work-order-form-boat-mooring-tag').value = boat.Tag_Number
      document.getElementById('work-order-form-boat-mooring-weight').value = boat.Mooring_Weight
      document.getElementById('work-order-form-boat-DMid').value = boat.DM_Boat
      storagePrice()
      document.getElementById('customer-search').value = ""
      getOpCodes()
      document.getElementById("customerData").style.display = "none";
      /*   popup.style.display = "none" */
      document.getElementById('boat-popup-div').style.display = 'none';
      syncWoContextStrip();
    })
    boatList.appendChild(listItem);

  });

  popup.appendChild(boatList);
  document.getElementById('boat-popup-div').style.display = 'flex';
}

// Rail › Email: the compose window (templates, lookup, drafts; mailCompose.js).
document.getElementById('email-open-button').addEventListener('click', () => openCompose({}));


document.getElementById('chat-send').addEventListener('click', sendChat)

function sendChat(e) {
  e.preventDefault();
  // get values to be submitted
  const timestamp = Date.now();
  const userId = auth.currentUser.uid;
  const messageInput = document.getElementById("message-input");
  const message = messageInput.value;
  // clear the input box
  messageInput.value = "";
  // auto scroll to bottom
  document.getElementById("messages").scrollIntoView({
    behavior: "smooth",
    block: "end",
    inline: "nearest",
  });
  const username = 'Mac';
  set(ref(database, "users/" + userId + "/messages/" + timestamp), {
    username,
    message,
  });
}


function partSelect() {

  var partTbody = document.getElementById("parts-list-body")

  partTbody.onclick = function (e) {
    e = e || window.event;
    var data = [];
    var target = e.srcElement || e.target;
    while (target && target.nodeName !== "TR") {
      target = target.parentNode;
    }
    if (target) {
      var cells = target.getElementsByTagName("td");
      for (var i = 0; i < cells.length; i++) {
        data.push(cells[i].innerHTML);
      }
    }

    var qtyPrompt = window.prompt("Enter Quantity");
    var activeQtyCell = document.getElementById('active-qty-cell');
    var activePartCell = document.getElementById('active-part-cell');

    if (activeQtyCell) {
      activeQtyCell.innerHTML = qtyPrompt;
      activeQtyCell.removeAttribute('id');  // Remove id
    }

    if (activePartCell) {
      activePartCell.innerHTML = data[1];
      activePartCell.removeAttribute('id');  // Remove id
    }

    document.getElementById("partsData").style.display = "none";
  }
}

function partOrderSelect(part) {

  let data = JSON.parse(part);

  let qtyPrompt = window.prompt("Enter Quantity")

  document.getElementById('partId').value = data.id
  document.getElementById('partData').setAttribute('data-part', JSON.stringify(data))
  document.getElementById('orderQty').value = qtyPrompt
  document.getElementById("orderPart").value = data.Part_Number
  document.getElementById("partDescription").value = data.Description
  document.getElementById("manufacturer").value = data.Manufacturer
  clearPickerSearch('myPartsInput')
  document.getElementById('partsOrderData').style.display = "none"
  document.getElementById('partVendorList').value = data.Vendor_List;


}

function partSelectData(e) {

  var partTbody = document.getElementById("parts-select-body")
  const row = e.target.closest('tr').querySelectorAll('td')
  const data = [];
  row.forEach(cell => data.push(cell.innerHTML))

  if (document.getElementById('#active-part-cell')) {
    document.querySelector("#active-part-cell").innerHTML = data[2]
  } else { addParts(data[2]) }
  clearPickerSearch('myPartsSelectInput')
  document.getElementById('partsSelectData').style.display = "none"


}

document.getElementById('exitPartsData').addEventListener('click', () => {
  document.getElementById('partsData').style.display = ""
  clearPickerSearch('myPartsInput')
})

document.getElementById('exitPartsOrderData').addEventListener('click', () => {
  document.getElementById('partsOrderData').style.display = ""
  clearPickerSearch('myPartsOrderInput')
})

document.getElementById('exitPartsSelectData').addEventListener('click', () => {
  document.getElementById('partsSelectData').style.display = ""
  clearPickerSearch('myPartsSelectInput')
})

document.getElementById('partButton').addEventListener('click', () => {
  document.getElementById('partsOrderData').style.display = "flex"
  console.log('Checked')
})

function calculateSuggestedSalePrice(listId, costId, saleId) {
  const msrp = parseFloat(document.getElementById(listId).value);
  const cost = parseFloat(document.getElementById(costId).value);
  const salePriceInput = document.getElementById(saleId);
  console.log(msrp, cost);
  if (isNaN(msrp) || isNaN(cost)) {
    salePriceInput.placeholder = '';
    return;
  }

  const percent = Math.round(((msrp - cost) / msrp) * 100);
  let suggestedSalePrice;

  if (percent <= 15) {
    suggestedSalePrice = msrp;
  } else if (percent <= 35) {
    suggestedSalePrice = msrp * 0.9; // 10% discount
  } else {
    suggestedSalePrice = msrp * 0.85; // 15% discount
  }

  salePriceInput.placeholder = `Suggested: $${suggestedSalePrice.toFixed(2)}`;
}

document.getElementById('listReceived').addEventListener('input', () => {
  let partMSRP = 'listReceived';
  let partCost = 'costReceived';
  let partSalePrice = 'saleReceived';
  calculateSuggestedSalePrice(partMSRP, partCost, partSalePrice);
});

document.getElementById('costReceived').addEventListener('input', () => {
  let partMSRP = 'listReceived';
  let partCost = 'costReceived';
  let partSalePrice = 'saleReceived';
  calculateSuggestedSalePrice(partMSRP, partCost, partSalePrice);
});

async function addPartOrder() {

  let partId = document.getElementById('partId').value;
  let tech = document.getElementById('partTech').value;
  let data = JSON.parse(document.getElementById('partData').getAttribute('data-part'));

  if (tech === "default") {
    toastMessage("Select a Tech", 'red');
    return;
  }

  let date = await formatDate();

  let id = partId ? partId : await generateId();
  let orderId = await generateId();
  let part = document.getElementById('orderPart').value.toUpperCase();
  let qty = document.getElementById('orderQty').value;
  let description = document.getElementById('partDescription').value;
  description = description.charAt(0).toUpperCase() + description.slice(1);
  let vendor = document.getElementById('vendor').value;
  let currentVendorList = document.getElementById('partVendorList').value;
  vendor = currentVendorList.includes(vendor) ? currentVendorList : currentVendorList + ',' + vendor;
  let manufacturer = document.getElementById('manufacturer').value;
  let location = data.Location;
  let jobId = document.getElementById('partJobId').value;
  let qtyReceived = 0;
  let listPrice = document.getElementById('listReceived').value;
  let cost = document.getElementById('costReceived').value;
  let sale = document.getElementById('saleReceived').value;

  let addToInventory = document.getElementById('addToInventory').checked;
  let partReceived = document.getElementById('receivePart').checked;

  if (partReceived == true) {
    qtyReceived = document.getElementById('qtyReceived').value;
  }

  let partData = {
    "Part_Number": part,
    "Manufacturer": manufacturer,
    "Vendor_List": [vendor],
    "Description": description,
    "Location": location,
    "id": id
  };

  if (addToInventory === true && partId === '') {
    partData.Add_Date = date;
    partData.Qty = 0;
  }

  if (partReceived === true && (addToInventory === true || partId !== '')) {
    partData.Qty = Number(qtyReceived) + Number(data.Qty);
    partData.Cost = cost;
    partData.Sale_Price = sale;
  }

  let expectedDate = document.getElementById('orderExpectedDate').value || '';

  // Add/update the inventory part record either way.
  await addOrUpdatePart(partData);

  // If the part is already in hand (checkbox checked above), this is just an
  // immediate inventory receipt — partData.Qty was already bumped above, and
  // there's nothing left "on order" to track an ETA for. Only create a
  // purchase order (the live Receiving-tab data model, /inventoryPOs — the
  // old Job_ID-linked /receiving store this used to write to has no reachable
  // review UI anymore, see wo_ops_list_redesign_built memory) when something
  // is genuinely still outstanding.
  if (!partReceived) {
    const poRef = await nextPurchaseOrderNumber();
    const placedDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const po = {
      id: orderId,
      vendor: document.getElementById('vendor').value,
      ref: poRef,
      placed: placedDate,
      open: true,
      orderedBy: tech,
      lines: [{
        partId: id,
        ordered: Number(qty) || 1,
        received: 0,
        bin: location || 'Unassigned',
        // Ties this line back to the operation that needed it — read by
        // resolveAttentionSlot()/resolveOperationSubline() (via
        // outstandingPartsForJob()) to build the "Parts on order · ETA …"
        // text on the Work Orders/Operations tabs.
        Job_ID: jobId,
        Expected_Date: expectedDate
      }]
    };
    await savePurchaseOrderRecord(po);
  }

  // Add a note to the job's Notes section
  const techData = await getCurrentUserData();  // Get current tech user data
  const noteId = await generateId();
  const noteDate = await getFormattedDate();
  const noteTime = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  
  const noteText = `Ordered part: ${part} (Qty: ${qty}), Tech: ${techData.displayName}, Date: ${noteDate}` + (expectedDate ? `, ETA: ${expectedDate}` : '');

  const newNote = {
    Note: noteText,
    Tech: techData.displayName,
    Date: noteDate,
    Time: noteTime,
    Job_ID: jobId,
    id: noteId
  };

  await addJobNote(jobId, newNote);

  // Clear the form inputs
  document.getElementById('orderPart').value = '';
  document.getElementById('orderQty').value = '';
  document.getElementById('partId').value = '';
  document.getElementById('partVendorList').value = '';
  document.getElementById('partData').removeAttribute('data-part');
  document.getElementById('partDescription').value = '';
  document.getElementById('addToInventory').checked = false;
  document.getElementById('receivePart').checked = false;
  document.getElementById('qtyReceived').value = '';
  document.getElementById('orderExpectedDate').value = '';
  document.getElementById('partWorkOrder').scrollIntoView();
}

async function addOrUpdatePart(partData) {
  try {
    const partRef = ref(database, `/inventory/${partData.id}`);
    await update(partRef, partData);
    await updateIndexedDB(partData, 'inventoryData');
    updateTimestamps('inventoryData', 'inventory');
  } catch (error) {
    console.error('Failed to add or update part:', error);
  }
}

async function updateIndexedDB(data, storeName) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();

      if (!data || !data.id) {
        reject("Data missing id");
        return;
      }

      const transaction = db.transaction(storeName, "readwrite");
      const objectStore = transaction.objectStore(storeName);

      const itemToUpdate = await new Promise((res, rej) => {
        const req = objectStore.get(data.id);
        req.onsuccess = (event) => res(event.target.result || null);
        req.onerror = (error) => {
          console.error("Error fetching data", error);
          rej(error);
        };
      });

      let patched = itemToUpdate;

      if (patched) {
        for (let key in data) {
          if (data.hasOwnProperty(key) && key !== 'id') {
            patched[key] = data[key];
          }
        }
      } else {
        patched = data;
      }

      await new Promise((res, rej) => {
        const req = objectStore.put(patched);
        req.onsuccess = () => res();
        req.onerror = (error) => {
          console.error("Error patching data", error);
          rej(error);
        };
      });

      console.log(`Data patched for ID ${data.id} in store ${storeName}`);
      window.dispatchEvent(new CustomEvent('indexedDBUpdated', { detail: { storeName, data } }));
      resolve();
    } catch (error) {
      console.log("Error updating IndexedDB", error);
      reject(error);
    }
  });
}


function partsList(dataList) {
  var Parent = document.getElementById('parts-list-body')
  while (Parent.hasChildNodes()) {
    Parent.removeChild(Parent.firstChild)
  }

  var Parent = document.getElementById('parts-kit-body')
  while (Parent.hasChildNodes()) {
    Parent.removeChild(Parent.firstChild)
  }

  Object.values(dataList).forEach(function (r) {

    var tbodyMain = document.getElementById("parts-list-body")
    var tbodyKit = document.getElementById('parts-kit-body')
    var tbodyOrder = document.getElementById('parts-order-body')
    var tbodySelect = document.getElementById('parts-select-body')
    var row = document.createElement("tr")
    row.setAttribute('data-jobInfo', JSON.stringify(r));
    var partId = document.createElement("td")
    partId.className = "main-data"
    partId.textContent = r.id
    var part = document.createElement("td")
    part.className = "main-data"
    part.textContent = r.Part_Number
    var description = document.createElement("td")
    description.className = "main-data"
    description.textContent = r.Description
    var location = document.createElement('td')
    location.className = 'main-data'
    location.textContent = r.Location

    row.appendChild(partId).style.display = 'none'
    row.appendChild(part)
    row.appendChild(description)
    row.appendChild(location)

    row.addEventListener('click', partSelect)

    tbodyMain.appendChild(row)
    var clonedRow = row.cloneNode(true);
    clonedRow.addEventListener('click', e => {
      const partInfo = JSON.parse(e.target.closest('tr').getAttribute('data-jobInfo'));
      const kitName = document.getElementById('currentPartKitName').value;
      addPartToPartsKit(partInfo, kitName);
    });

    tbodyKit.appendChild(clonedRow);
    var clonedRow2 = row.cloneNode(true);
    clonedRow2.addEventListener('click', e => {
      partOrderSelect(e.target.closest('tr').getAttribute('data-jobInfo'))
    });

    tbodyOrder.appendChild(clonedRow2);
    var clonedRow3 = row.cloneNode(true);
    clonedRow3.addEventListener('click', partSelectData)

    tbodySelect.appendChild(clonedRow3);
  })
}

document.getElementById('customerSearchBar').addEventListener('input', () => {
  searchCustomers('customerSearchBar');
});

document.getElementById('customer-search').addEventListener('input', () => {
  searchCustomers('customer-search');
});

function resetSearch(tableId) {
  var tableRows = document.querySelectorAll('#' + tableId + ' tbody tr');

  for (let row of tableRows) {
    row.style.display = 'table-row';
  }
}

function searchCustomers(searchBar) {

  const searchQuery = document.getElementById(searchBar).value.toLowerCase().trim();
  const table = document.getElementById(searchBar).getAttribute('data-table');
  const searchTerms = searchQuery.split(' ');

  var tableRows = document.querySelectorAll('#' + table + ' tbody tr');

  for (let row of tableRows) {
    const rowCells = row.querySelectorAll('td');
    let matchFound = true;

    for (const searchTerm of searchTerms) {
      let termMatchFound = false;

      for (const cell of rowCells) {
        const cellText = cell.textContent.toLowerCase();

        if (cellText.includes(searchTerm)) {
          termMatchFound = true;
          break;
        }
      }

      if (!termMatchFound) {
        matchFound = false;
        break;
      }
    }

    if (matchFound) {
      row.style.display = 'table-row';
    } else {

      if (!row.classList.contains('header')) {
        row.style.display = 'none';
      }

    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('exitCustomerBoat').addEventListener('click', () => {
    document.getElementById('currentCustomerTable').style.display = "none"
  })

  const modals = document.querySelectorAll('.modal')

  modals.forEach(modal => {
      modal.addEventListener('click', (e) => {
        if (e.target.classList.contains('modal')) {
          modal.style.display = 'none'
        }
      })
    });
})

document.getElementById('open-new-customer-form').addEventListener('click', openNewCustomerForm)
document.getElementById('open-new-boat-form').addEventListener('click', openCustomerData)

function openCustomerData() {
  document.getElementById('currentCustomerTable').style.display = "flex"
  document.getElementById('customerPopUp').style.display = "none"
}

function openNewCustomerForm() {
  document.getElementById('addNewCustomer').style.display = "flex"
  document.getElementById('customerPopUp').style.display = "none"
}

async function getFormattedDate(date) {
  return new Promise((resolve) => {
    const dateObj = date ? new Date(date) : new Date();
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    resolve(formattedDate);
  });
}

document.getElementById('receivePart').addEventListener('click', () => {
  if (document.getElementById('receivePart').checked === true) {
    document.getElementById('listReceived').value = '';
    document.getElementById('costReceived').value = '';
    document.getElementById('saleReceived').value = '';
    document.getElementById('listReceived').focus();

    var qty = prompt('Enter Quantity Received')
    document.getElementById('qtyReceived').value = qty
  } else { document.getElementById('qtyReceived').value = '' }

})

function clearPartOrder() {

  document.getElementById('orderPart').value = ''
  document.getElementById('orderQty').value = ''
  document.getElementById('partDescription').value = ''
  document.getElementById('vendor').value = ''
  document.getElementById('vendorId').value = ''
  document.getElementById('manufacturer').value = ''
  document.getElementById('newPart').value = "Yes"
  document.getElementById('orderedPart').checked = false
  document.getElementById('addToInventory').checked = false
  document.getElementById('receivePart').checked = false
  document.getElementById('qtyReceived').value = ''
  document.getElementById('partWorkOrder').scrollIntoView()

}

document.addEventListener('DOMContentLoaded', () => {
  const buttons = document.querySelectorAll('.exitButton');
  buttons.forEach(button => {
    button.addEventListener('click', e => {

      const targetButton = e.target.closest('button');

      if (targetButton) {
   
        if(e.target.closest('button').getAttribute('data-return')){

          if(e.target.closest('button').getAttribute('data-return') === 'spring'){
    
            document.getElementById('spring').style.display = 'flex';
            document.getElementById('spring').style.visibility = 'visible';

            document.getElementById('open-work').style.display = 'none';
            document.getElementById('open-work').style.visibility = 'hidden';

            e.target.closest('button').removeAttribute('data-return');
            document.getElementById('add-btn').removeAttribute('data-return');
            document.getElementById(e.target.closest('button').getAttribute('data-close')).style.display = 'none';
            document.querySelector('.work-order-type').style.display = 'flex';

          }else if(e.target.closest('button').getAttribute('data-return') === 'hauling'){
    
            document.getElementById('hauling').style.display = 'flex';
            document.getElementById('hauling').style.visibility = 'visible';

            document.getElementById('open-work').style.display = 'none';
            document.getElementById('open-work').style.visibility = 'hidden';

            e.target.closest('button').removeAttribute('data-return');
            document.getElementById('add-btn').removeAttribute('data-return');
            document.getElementById(e.target.closest('button').getAttribute('data-close')).style.display = 'none';
            document.querySelector('.work-order-type').style.display = 'flex';

          }else if(e.target.closest('button').getAttribute('data-return') === 'customer'){
    
            document.getElementById('customer').style.display = 'flex';
            document.getElementById('customer').style.visibility = 'visible';

            document.getElementById('open-work').style.display = 'none';
            document.getElementById('open-work').style.visibility = 'hidden';

            e.target.closest('button').removeAttribute('data-return');
            document.getElementById(e.target.closest('button').getAttribute('data-close')).style.display = 'none';
            document.querySelector('.work-order-type').style.display = 'flex';

        }
    
        }
        
        if (targetButton.hasAttribute('data-close')) {
          const elementToClose = document.getElementById(targetButton.getAttribute('data-close'));
          console.log(elementToClose);
          if(elementToClose.id === "Add"){ 
            currentOpCodes = [];
            document.getElementById('work-order-type-select').value = 'general-work-order'

            const storage = document.getElementById('winter-storage-section')
            const winterSpring = document.getElementById('storage-work-order-section')
            const dates = document.getElementById('work-order-date-section')
            const general = document.getElementById('general-work-order-section')
            
              storage.classList.add('hidden-form-section');
              dates.classList.add('hidden-form-section');
              winterSpring.classList.add('hidden-form-section')
              winterSpring.style.display='none';
              general.classList.remove('hidden-form-section');
              general.style.display='flex';

          }

          if (elementToClose) {
            elementToClose.style.display = 'none';

            if(targetButton.getAttribute('data-close') === 'Add') {
              closeAdd();
            }

          }

        }else if (targetButton.hasAttribute('data-slide')) {
          const elementToSlide = document.getElementById(targetButton.getAttribute('data-slide'));
          if (elementToSlide) {
            elementToSlide.classList.remove('active');
   
            if(elementToSlide.id === 'office-notes'){
              document.querySelectorAll('.note-active').forEach(note => note.classList.remove('note-active'));
            }

          }
          
        }
      }
    });
  });
});


document.addEventListener("DOMContentLoaded", () => {

  const buttons = document.querySelectorAll('.tideButton')
  buttons.forEach(button => {
    button.addEventListener('click', (e) => {
      const elements = document.querySelectorAll('.selected-tide-button');
      elements.forEach(element => element.classList.remove('selected-tide-button'));
      e.currentTarget.classList.add('selected-tide-button');
    })

  })

})

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('svg').forEach(function (svg) {
    svg.addEventListener('click', function () {
      this.classList.toggle('toggle');
    });
  });
});

async function fetchDataFromStore(storeName) {
  try {
    const db = await getDB();
    const tx = db.transaction([storeName], 'readonly');
    const store = tx.objectStore(storeName);

    const data = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onerror = () => {
        console.error('Error fetching data:', req.error);
        reject(req.error);
      };
      req.onsuccess = () => resolve(req.result || []);
    });

    return data;
  } catch (error) {
    console.error('Database error:', error);
    throw error;
  }
}


async function populateVendorLists() {
  const data = await fetchDataFromStore('vendorData');
  const vendorList = Object.values(data).filter(vendor => vendor.Vendor_Name && !vendor.Inactive).map(vendor => vendor.Vendor_Name);

  const vendorInputs = document.querySelectorAll('.vendor-select');

  vendorInputs.forEach(input => {
    // Create a wrapper div for positioning context
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(input);

    const suggestionContainer = document.createElement('div');
    suggestionContainer.classList.add('suggestion-list');
    suggestionContainer.style.display = 'none';
    suggestionContainer.style.position = 'absolute';
    suggestionContainer.style.width = '100%';
    suggestionContainer.style.top = '100%';
    suggestionContainer.style.left = '0';
    wrapper.appendChild(suggestionContainer);

    input.addEventListener('input', async function () {
      const query = this.value.toLowerCase().trim();
      suggestionContainer.innerHTML = ''; // Clear previous suggestions

      const filteredVendors = vendorList.filter(vendor =>
        vendor.toLowerCase().includes(query)
      );

      filteredVendors.forEach(vendor => {
        const suggestionItem = document.createElement('div');
        suggestionItem.classList.add('suggestion-item');
        suggestionItem.textContent = vendor;
        suggestionItem.addEventListener('click', () => {
          input.value = vendor;
          suggestionContainer.style.display = 'none';
        });
        suggestionContainer.appendChild(suggestionItem);
      });

      suggestionContainer.style.display = filteredVendors.length ? 'flex' : 'none';
    });

    // Hide suggestions when clicking outside
    document.addEventListener('click', function (e) {
      if (e.target !== input && !suggestionContainer.contains(e.target)) {
        suggestionContainer.style.display = 'none';
      }
    });
  });
}

function callOpenCustomerData(customerId, from) {

const pageView = document.getElementById('current-page').innerHTML;
console.log(pageView);
let view = null;

  if(pageView === 'Work Orders'){
    view = 'work-order-view';
  }else if(pageView === 'Operations'){
    view = 'job-view';
  }

  const customersIframe = document.getElementById('customers').querySelector('iframe');

  if (customersIframe) {
    customersIframe.contentWindow.postMessage({
      type: 'customerLookup',
      customerId: customerId,
      return: view
    }, '*');
  }

  const elements = document.querySelectorAll('.page-view')
  elements.forEach(page => {
    page.style.display = 'none';
    page.style.visibility = 'hidden';
  });

  document.getElementById('customers').style.display = 'flex';
  document.getElementById('customers').style.visibility = 'visible';
  document.getElementById('current-page').innerHTML = "Customer Database";
}

function callNewCustomerAdded(){
  const customersIframe = document.getElementById('customers').querySelector('iframe');
  if (customersIframe) {
    customersIframe.contentWindow.postMessage({
      type: 'addedNewCustomer',
    }, '*');
  }
}

/* ===== DASHBOARD CLICK-THROUGH ==================================
   dashboard.js sends { from:'dashboard', type:'goTo', page, filter } — filter
   is null for a plain "open this page" link, or an object describing exactly
   what to land on (a work order, a status, a tab, a date, a date range). This
   section resolves that filter into real UI state for the native Work
   Orders/Storage Contracts/Closed Operations views (iframe-hosted pages get
   the same filter forwarded on via postMessage once their page is showing —
   see the 'goTo' handler below). */

// Same heuristic as dashboard.js's isOverdue(): open, and its most recent
// date field is more than 3 days old. Kept in sync manually — dashboard.js
// runs in its own iframe/webpack entry with no shared module graph.
function isWorkOrderRowOverdue(data) {
  if (data.Status !== 'In Progress' && data.Status !== 'Needs Scheduling') return false;
  const dateStr = data.Schedule_Date || data.Request_Date;
  if (!dateStr) return false;
  const date = parseDay(dateStr);
  if (isNaN(date)) return false;
  const threshold = new Date();
  threshold.setHours(0, 0, 0, 0);
  threshold.setDate(threshold.getDate() - 3);
  return date < threshold;
}

// Generic predicate-based row filter for the Work Orders table — used for
// dashboard-driven filters (like "overdue") that have no matching checkbox in
// the real #filter-view panel, so applyFilters()/toggleWorkOrders() can't
// express them directly.
function filterWorkOrderRowsByPredicate(predicate) {
  document.querySelectorAll('#table-content .table-item').forEach(row => {
    let data;
    try { data = JSON.parse(row.getAttribute('data-jobinfo') || '{}'); } catch (err) { data = {}; }
    const show = predicate(data);
    row.classList.toggle('filtered', !show);
    row.style.display = show ? '' : 'none';
  });
  updateTable();
}

// Closed Operations (#closedJobList) isn't one of the views applyFilters()
// touches (it only reads #table-content/#jobList),
// so the Revenue KPI's week-range deep link needs its own date match here.
function filterClosedJobsByDateRange(startStr, endStr) {
  const start = new Date(startStr);
  const end = new Date(endStr);
  document.querySelectorAll('#closedJobList .job-item').forEach(row => {
    let data;
    try { data = JSON.parse(row.getAttribute('data-jobinfo') || '{}'); } catch (err) { data = {}; }
    const dateStr = data.Start_Date || data.Request_Date;
    const date = dateStr ? new Date(dateStr) : null;
    row.style.display = (date && !isNaN(date) && date >= start && date <= end) ? '' : 'none';
  });
}

// Routes a Dashboard "open-work" deep link to the right sub-tab/filter state.
// filter is the payload described above; null/absent reproduces the original
// unfiltered "just open Work Orders, wherever the user last left it" behavior.
async function applyOpenWorkDashboardNav(filter) {
  if (!filter) {
    document.querySelectorAll('.page-view').forEach(page => {
      page.style.display = 'none';
      page.style.visibility = 'hidden';
    });
    document.getElementById('open-work').style.display = 'flex';
    document.getElementById('open-work').style.visibility = 'visible';
    document.getElementById('work-order-view').style.display = 'flex';
    document.getElementById('current-page').innerHTML = 'Work Orders';
    switchView(currentJobView);
    return;
  }

  if (filter.workOrder) {
    // Guarantee the target row is actually visible regardless of whatever
    // filter/search the user happened to leave active last time they were here.
    await clearFilters();
    const searchBox = document.getElementById('search');
    if (searchBox) searchBox.value = '';
    await jumpToWorkOrder(filter.workOrder);
    return;
  }

  if (filter.view === 'contracts') {
    await switchView('contracts');
    if (filter.unsignedOnly) {
      setContractsUnsignedOnly(true);
      document.getElementById('contracts-unsigned-toggle')?.classList.add('active');
      renderStorageContractsList();
    }
    return;
  }

  if (filter.view === 'shrinkwrap') {
    setShrinkwrapStage(filter.stage || 'all');
    await switchView('shrinkwrap');
    return;
  }

  if (filter.view === 'canvas') {
    setCanvasFilter({ overdue: !!filter.overdue });
    await switchView('canvas');
    if (filter.search) {
      const s = document.getElementById('canvas-search');
      s.value = filter.search;
      s.dispatchEvent(new Event('input'));
    }
    return;
  }

  if (filter.view === 'internal') {
    await switchView('internal');
    return;
  }

  if (filter.view === 'closed') {
    await switchView('closed');
    if (filter.dateRange) filterClosedJobsByDateRange(filter.dateRange.start, filter.dateRange.end);
    return;
  }

  await clearFilters();
  const searchBox = document.getElementById('search');
  if (searchBox) searchBox.value = '';
  await switchView('workOrder');

  if (filter.status === 'overdue') {
    filterWorkOrderRowsByPredicate(isWorkOrderRowOverdue);
    toastMessage('Showing overdue work orders', '#0C2C49');
  } else if (filter.status === 'hold') {
    const cb = document.getElementById('filter-on-schedule');
    if (cb) { cb.checked = true; cb.dispatchEvent(new Event('change')); }
  } else if (filter.status === 'unassigned') {
    const cb = document.getElementById('filter-tech-not-assigned');
    if (cb) { cb.checked = true; cb.dispatchEvent(new Event('change')); }
  } else if (filter.dateRange) {
    document.getElementById('start-date').value = filter.dateRange.start;
    document.getElementById('end-date').value = filter.dateRange.end;
    document.getElementById('start-date').dispatchEvent(new Event('change'));
  }
}

window.addEventListener('message', async (event) => {
  const { type, from, returnView } = event.data;

  /* ===== MAPS =================================================== */
  if (from === 'maps') {
    if (type === 'updateBoatLocation') {
      updateBoatInfo(event.data.customerId,
                     event.data.boatId,
                     event.data.index,
                     event.data.value);
    }
  }

  /* ===== SCHEDULE =============================================== */
  else if (from === 'schedule') {
    if (type === 'showMap') {
      await showPage('maps');
      const iframe = ensureMapsIframe();
      if (iframe && (iframe.dataset.loaded || iframe.contentDocument?.readyState === 'complete')) {
        iframe.dataset.loaded = '1';
        iframe.contentWindow.postMessage({ action: 'renderMaps' }, '*');
      }
    }
  }

  /* ===== CUSTOMERS ============================================== */
  else if (from === 'customers') {

    if (type === 'customerAdded') {
      // make sure the rentalDatabase iframe exists, then post to it
      const rentalWin = getIframeWindow('rentalDatabase');
      if (rentalWin) {
        rentalWin.postMessage(
          { type: 'generateCustomerTable', from: 'main' },
          '*'
        );
      }
      fetchCustomerDataAndGenerateTable();
    }

    if (type === 'returnView') {
      
      if(returnView === 'null' || returnView === null || returnView === undefined){
        return;
      }

      if (returnView === 'haulLaunch') {
        showPage('haulLaunch');
      }  else {
        const elements = document.querySelectorAll('.page-view')
        elements.forEach(page => {
          page.style.display = 'none';
          page.style.visibility = 'hidden';
        });
        document.getElementById('open-work').style.display = 'flex';
        document.getElementById('open-work').style.visibility = 'visible';
        document.getElementById('work-order-view').style.display = 'flex';
        document.getElementById('current-page').innerHTML = "Work Orders";
        switchView(currentJobView);      // overlay stays on top
      }

      const pageNames = {
        'work-order-view': 'Work Order',
        'job-view'      : 'Operations'
      };

      document.getElementById('current-page').textContent =
          pageNames[returnView] || returnView;
    }

    if (type === 'haulDate')
      updateJobDatesBasedOnBoatId(event.data.boatId,
                                  'Haul_Date',
                                  event.data.date);

    if (type === 'launchDate')
      updateJobDatesBasedOnBoatId(event.data.boatId,
                                  'Launch_Date',
                                  event.data.date);
  }

  /* ===== DASHBOARD =============================================== */
  // The Battery Bench uses the same deep link to open a replacement's work order; Scheduler › Tasks
  // uses it to open a work run on the Yard Map.
  else if (from === 'dashboard' || from === 'battery' || from === 'equipment' || from === 'scheduler') {
    if (type === 'goTo' && event.data.page === 'maps') {
      // The Yard Map draws its boats on a message of its own (the rail's map button sends it), so open it
      // that way, then hand it the run (or Work mode) once the page has loaded (maps.js, openWorkRun).
      document.querySelectorAll('.selected-tide-button').forEach(b => b.classList.remove('selected-tide-button'));
      document.getElementById('map-open-button')?.classList.add('selected-tide-button');
      document.getElementById('map-open-button')?.click();
      const f = event.data.filter || {};
      for (let i = 0; i < 40; i++) {
        const frame = document.getElementById('maps')?.querySelector('iframe');
        if (frame?.dataset.loaded) { frame.contentWindow.postMessage({ action: 'openWorkRun', runId: f.workRun || null }, window.location.origin); break; }
        await new Promise(r => setTimeout(r, 250));
      }
    } else if (type === 'goTo' && event.data.page === 'invoices') {
      // The Invoices window isn't a page: open it over whatever is showing.
      openInvoicesList(event.data.filter?.tab || 'accounts', event.data.filter?.search);
    } else if (type === 'goTo' && event.data.page === 'open-work') {
      // 'open-work' is a native page-view, not an iframe-hosted page, so it
      // can't go through showPage() (which assumes every PAGE_PATHS target
      // is an iframe and would inject a broken <iframe src="undefined">
      // into it). applyOpenWorkDashboardNav() also resolves event.data.filter
      // into the right sub-tab/status/date-range/specific-work-order state.
      await applyOpenWorkDashboardNav(event.data.filter);
    } else if (type === 'goTo' && event.data.page) {
      await showPage(event.data.page);
      // Iframe-hosted pages get their filter forwarded the same way inventory.js/
      // fuelDashboard.js/mooringDatabase.js already listen for it — showPage()
      // only (re)creates the iframe on its first visit and otherwise just
      // toggles visibility, so this can't be a URL param on the src.
      if (event.data.filter) {
        const iframe = document.getElementById(event.data.page)?.querySelector('iframe');
        iframe?.contentWindow?.postMessage({ type: 'dashboardFilter', filter: event.data.filter }, '*');
      }
    }
  }

  /* ===== MOORING FIELD =========================================== */
  // Place › "Assign {mooring} to {boat}" opens Rental Services with it filled in. The
  // rental iframe may be brand new, so post once it has loaded.
  /* ===== MESSAGES ============================================== */
  else if (from === 'messages' || from === 'requests') {
    if (type === 'openCustomer' && event.data.customerId) {
      document.querySelectorAll('.selected-tide-button').forEach(b => b.classList.remove('selected-tide-button'));
      document.getElementById('customers-open-button')?.classList.add('selected-tide-button');
      await showPage('customers');
      document.getElementById('current-page').textContent = 'Customers';
      const iframe = document.getElementById('customers')?.querySelector('iframe');
      iframe?.contentWindow?.postMessage({ type: 'customerLookup', customerId: event.data.customerId, return: null }, window.location.origin);
    }
  }

  /* ===== TECH QUESTIONS (techQuestions.js) ===================== */
  // Open the thread's work order, one of its operations (e.g. found work just made, to price it),
  // or the Add operation form for it.
  else if (from === 'techQuestions') {
    const wo = event.data.workOrder || event.data.filter?.workOrder;
    const toWorkOrder = async () => {
      document.querySelectorAll('.selected-tide-button').forEach(b => b.classList.remove('selected-tide-button'));
      document.getElementById('open-work-button')?.classList.add('selected-tide-button');
      await applyOpenWorkDashboardNav({ workOrder: wo });
      for (let i = 0; i < 20 && !document.querySelector('#operations-table .op-table-data-row'); i++) await new Promise(r => setTimeout(r, 250));
    };
    if (type === 'goTo' && wo) await toWorkOrder();
    else if (type === 'openOp' && wo && event.data.jobId) {
      await toWorkOrder();
      let job = null;
      for (let i = 0; i < 20 && !job; i++) { job = await getJobData(event.data.jobId).catch(() => null); if (!job) await new Promise(r => setTimeout(r, 300)); }
      if (job) opCheck('', { ...job, Job_ID: job.Job_ID || event.data.jobId });
    } else if (type === 'addOperation' && wo) {
      await toWorkOrder();
      if (document.querySelector('#operations-table .op-table-data-row')) addOperation();
    }
  }

  else if (from === 'mooringField') {
    if (type === 'goTo' && event.data.page) {
      await showPage(event.data.page);
      const iframe = document.getElementById(event.data.page)?.querySelector('iframe');
      if (iframe && (event.data.filter || event.data.hash)) {
        const post = () => {
          // Moorings tabs: "Season" opens the Mooring Field in that mode.
          if (event.data.hash) iframe.contentWindow.location.hash = event.data.hash;
          if (event.data.filter) iframe.contentWindow?.postMessage({ type: 'dashboardFilter', filter: event.data.filter }, '*');
        };
        let loaded = false;
        try { loaded = iframe.contentDocument?.readyState === 'complete' && iframe.contentWindow.location.href !== 'about:blank'; } catch { loaded = false; }
        if (loaded) post(); else iframe.addEventListener('load', post, { once: true });
      }
    }
  }

  /* ===== COMPLIANCE ============================================== */
  // Certifications links an equipment inspection to its unit on Equipment.
  else if (from === 'compliance') {
    if (type === 'goTo' && event.data.page === 'equipment') await openEquipmentPage(event.data.filter);
    else if (type === 'goTo' && event.data.page === 'uscg') {
      // Overview's "before the USCG inspection" lines.
      await showPage('uscg');
      document.getElementById('current-page').textContent = 'Launch boats';
    }
  }

  // Launch boats: "waiting for a decision in Equipment › To do" opens Equipment's To do.
  else if (from === 'uscg') {
    if (event.origin === window.location.origin && type === 'goTo' && event.data.page === 'equipment') await openEquipmentPage(event.data.filter);
  }

  /* ===== HAUL/LAUNCH ============================================= */
  /* ===== MOORING HAULS / LAUNCHES (bottom-nav "Map") ============ */
  else if (from === 'mooringOps') {
    if (type === 'goTo' && event.data.page === 'launchDispatch') {
      await showPage('launchDispatch');
      document.getElementById('current-page').innerHTML = 'Launch Dispatch';
    }
  }

  else if (from === 'haulLaunch') {
    if (type === 'goTo' && event.data.page === 'open-work') {
      // With filter.workOrder this opens that work order itself, not
      // whatever list view the user last had open.
      await applyOpenWorkDashboardNav(event.data.filter);
    } else if (type === 'goTo' && event.data.page) {
      showPage(event.data.page);
    }
    if (type === 'opCheck') opCheck('', event.data.info);
  }
});



document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('.update-form');

  // One timer per field (a shared one dropped the first of two quick edits), and the record is
  // taken when the person types: by the time the save runs they may have opened another one.
  elements.forEach(element => {
    let debounceTimer;
    // Create the icon element
    const iconElement = document.createElement('span');
    iconElement.className = 'update-icon';

    // Insert the icon after the element
    element.parentNode.insertBefore(iconElement, element.nextSibling);

    element.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      iconElement.textContent = '🔄';
      iconElement.classList.add('updating');
      iconElement.classList.remove('updated');

      const jobId = document.getElementById('currentJobId').value;
      const customerId = document.getElementById('updateCustomerId').value;
      const jobIndex = e.target.getAttribute('data-update');
      const customerIndex = e.target.getAttribute('data-update-customer');
      const value = e.target.value;
      debounceTimer = setTimeout(async () => {
      
        const haulingIframe = document.querySelector('#hauling iframe'); // Find the iframe inside your div
        if (haulingIframe) {
          const iframeDocument = haulingIframe.contentDocument || haulingIframe.contentWindow.document;
          if(iframeDocument.querySelector('.haul-item[data-id="' + jobId + '"]')){
          let operationData = JSON.parse(iframeDocument.querySelector('.haul-item[data-id="' + jobId + '"]')?.getAttribute('data-jobInfo'));
          operationData[jobIndex] = value;
          iframeDocument.querySelector('.haul-item[data-id="' + jobId + '"]')?.setAttribute('data-jobInfo', JSON.stringify(operationData));
          }
        } else {
          console.log("Hauling not found");
        }

        const launchingIframe = document.querySelector('#spring iframe'); // Find the iframe inside your div
        if (launchingIframe) {
          const iframeDocument = launchingIframe.contentDocument || launchingIframe.contentWindow.document;
          if(iframeDocument.querySelector('.launch-item[data-id="' + jobId + '"]')){
          let operationData = JSON.parse(iframeDocument.querySelector('.launch-item[data-id="' + jobId + '"]')?.getAttribute('data-jobInfo'));
          operationData[jobIndex] = value;
          iframeDocument.querySelector('.launch-item[data-id="' + jobId + '"]')?.setAttribute('data-jobInfo', JSON.stringify(operationData));
          }
        } else {
          console.log("Spring not found");
        }

        if(document.querySelector('.job-item[data-id="' + jobId + '"]')) {
        let operationData = JSON.parse(document.querySelector('.job-item[data-id="' + jobId + '"]')?.getAttribute('data-jobInfo'));
        operationData[jobIndex] = value;
        document.querySelector('.job-item[data-id="' + jobId + '"]')?.setAttribute('data-jobInfo', JSON.stringify(operationData));
        }

        if(document.querySelector('.haul-item[data-id="' + jobId + '"]')) {
          let operationData = JSON.parse(document.querySelector('.haul-item[data-id="' + jobId + '"]')?.getAttribute('data-jobInfo'));
          operationData[jobIndex] = value;
          document.querySelector('.haul-item[data-id="' + jobId + '"]')?.setAttribute('data-jobInfo', JSON.stringify(operationData));
          }

        if(document.querySelector('.active-work-order')){
          let workOrderData = JSON.parse(document.querySelector('.active-work-order')?.getAttribute('data-jobInfo'));
          workOrderData[jobIndex] = value;
          document.querySelector('.active-work-order')?.setAttribute('data-jobInfo', JSON.stringify(workOrderData));
        }

        if(document.querySelector('.op-table-data-row[data-id="' + jobId + '"]')){
        let workOrderOpData = JSON.parse(document.querySelector('.op-table-data-row[data-id="' + jobId + '"]')?.getAttribute('data-jobInfo'));
        workOrderOpData[jobIndex] = value;
        document.querySelector('.op-table-data-row[data-id="' + jobId + '"]')?.setAttribute('data-jobInfo', JSON.stringify(workOrderOpData));
        };

  
        try {
          if (jobIndex) {
            await updateJobInfo(jobId, jobIndex, value);
          } else if (customerIndex) {
            await updateCustomerInfo(customerId, customerIndex, value);
          }

          iconElement.textContent = '✅';
          iconElement.classList.remove('updating');
          iconElement.classList.add('updated');

          // Remove the 'updated' class and clear the icon after 1 second
          setTimeout(() => {
            iconElement.classList.remove('updated');
            iconElement.textContent = '';
          }, 1000);
        } catch (error) {
          console.error('Error updating info:', error);
          iconElement.classList.remove('updating');
          iconElement.textContent = '❌';
          toastMessage('Failed to update info. Please try again.', 'red');
        }
      }, 2000);
    });
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('.work-order-form-customer .update-customer-form');

  // Per field, and the customer taken when the person types (see .update-form above): the form
  // may be closed, its ids cleared, before the save runs.
  elements.forEach(element => {
    let debounceTimer;
    // Create the icon element
    const iconElement = document.createElement('span');
    iconElement.className = 'update-icon';

    // Insert the icon after the element
    element.parentNode.insertBefore(iconElement, element.nextSibling);

    element.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      iconElement.textContent = '🔄';
      iconElement.classList.add('updating');
      iconElement.classList.remove('updated');

      const customerId = document.getElementById('work-order-form-customer-id').value;
      const customerIndex = e.target.getAttribute('data-update-customer');
      const value = e.target.value;
      debounceTimer = setTimeout(async () => {
        if (!customerId) { iconElement.textContent = ''; iconElement.classList.remove('updating'); return; }
        try {

            await updateCustomerInfo(customerId, customerIndex, value);
            console.log(`Updated customer ${customerIndex} to ${value} for customer ${customerId}`);

          iconElement.textContent = '✅';
          iconElement.classList.remove('updating');
          iconElement.classList.add('updated');

          setTimeout(() => {
            iconElement.classList.remove('updated');
            iconElement.textContent = '';
          }, 1000);
        } catch (error) {
          console.error('Error updating info:', error);
          iconElement.classList.remove('updating');
          iconElement.textContent = '❌';
          toastMessage('Failed to update info. Please try again.', 'red');
        }
      }, 2000);
    });
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('.work-order-form-boat .update-boat-form');

  // Per field, and the boat taken when the person types (see .update-form above). With the ids
  // read when the timer fired, closing the form first saved to customers//Boats// (a stray
  // /customers/Boats record).
  elements.forEach(element => {
    let debounceTimer;
    // Create the icon element
    const iconElement = document.createElement('span');
    iconElement.className = 'update-icon';

    // Insert the icon after the element
    element.parentNode.insertBefore(iconElement, element.nextSibling);

    element.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      iconElement.textContent = '🔄';
      iconElement.classList.add('updating');
      iconElement.classList.remove('updated');

      const customerId = document.getElementById('work-order-form-customer-id').value;
      const boatId = document.getElementById('work-order-form-boat-id').value;
      const customerIndex = e.target.getAttribute('data-update-boat');
      const value = e.target.value;
      debounceTimer = setTimeout(async () => {
        if (!customerId || !boatId) { iconElement.textContent = ''; iconElement.classList.remove('updating'); return; }
        try {

            await updateBoatInfo(customerId, boatId, customerIndex, value);
            console.log(`Updated customer ${customerIndex} to ${value} for customer ${customerId}`);

          iconElement.textContent = '✅';
          iconElement.classList.remove('updating');
          iconElement.classList.add('updated');

          setTimeout(() => {
            iconElement.classList.remove('updated');
            iconElement.textContent = '';
          }, 1000);
        } catch (error) {
          console.error('Error updating info:', error);
          iconElement.classList.remove('updating');
          iconElement.textContent = '❌';
          toastMessage('Failed to update info. Please try again.', 'red');
        }
      }, 2000);
    });
  });
});

document.getElementById('open-billing').addEventListener('click', async () => {
  const jobId = document.getElementById('jobId').value;
  const jobInfo = await getJobData(jobId);
  await billingModal(jobInfo);
});

document.getElementById('daily-log-button').addEventListener('click',async () => {

  if(globalTechStats === undefined || globalTechStats === null){
    await updateWeekView();
  }

  const elements = document.querySelectorAll('.page-view')
  elements.forEach(page => {
    page.style.display = 'none';
    page.style.visibility = 'hidden';
  });

  const dailyLogDate = document.getElementById('daily-log-date');

  if (dailyLogDate.value === '') {
    dailyLogDate.value = formatYMDLocal(new Date());
  }

loadDailyLog('daily');

  
  document.getElementById('daily-log-div').style.display = 'flex';
  document.getElementById('daily-log-div').style.visibility = 'visible';
  document.getElementById('current-page').innerHTML = "Daily Log";
});

async function getAllJobData() {
  try {
    const db = await getDB();
    const tx = db.transaction(['jobData'], 'readonly');
    const store = tx.objectStore('jobData');

    const result = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result || null);
    });

    return result && result.length ? result : null;
  } catch (error) {
    throw new Error(`Error getting job data: ${error?.message || error}`);
  }
}


// Listen for messages from iframes
window.addEventListener('message', (event) => {

  if (event.data === 'closeMenus') {
      closeUserMenu();
  }
});

document.getElementById('change-boat-button').addEventListener('click', () => {
  changeBoat()}
);

async function changeBoat() {

  document.getElementById('update-boat-list').innerHTML = '';

  const customerId = document.getElementById('update-work-order-customer-id').value;

  const customerInfo = await getCustomerDataById(customerId).catch(() => null);
  const boatData = customerInfo?.customerData?.Boats || {};
  if (!Object.keys(boatData).length) {
    toastMessage('No boats on file for this customer — add one on the customer record first.', 'red');
    return;
  }

  Object.values(boatData).forEach(boat => {
    const boatButton = document.createElement('button');
    boatButton.className = 'boat-button';
    boatButton.textContent = boat.Boat_Name;
    boatButton.setAttribute('data-boat-id', boat.Boat_ID);
    boatButton.setAttribute('data-customer-id', customerId);

    boatButton.addEventListener('click', async () => {
      const boatId = boatButton.getAttribute('data-boat-id');
      const customerId = boatButton.getAttribute('data-customer-id');
      const jobIdList = Array.from(document.querySelectorAll('.checkbox-item input[type="checkbox"]'))
      .map(checkbox => checkbox.getAttribute('data-jobid'));
  

      jobIdList.forEach(async jobId => {
        const jobRef = ref(database, `/data/${jobId}`);
        await update(jobRef, { Boat_ID: boatId, Customer_ID: customerId, Boat_Name: boatButton.textContent });
      });

      toastMessage('Boat updated successfully', 'green', 2000);
      document.getElementById('update-boat-div').style.display = 'none';
    });

    document.getElementById('update-boat-list').appendChild(boatButton);

  });

  document.getElementById('update-boat-div').style.display = 'flex';

}



window.addEventListener('DOMContentLoaded', () => {
  const openScannerButton = document.getElementById('open-scanner');

  if (!('BarcodeDetector' in window)) {
    console.warn('Barcode Detector API not supported. Hiding scanner button.');
    openScannerButton.style.display = 'none'; // or use .disabled = true
  }

});


document.getElementById('open-scanner').addEventListener('click', async function() {
  const scannerContainer = document.getElementById('barcodeScanner');
  const video = document.getElementById('video');
  const output = document.getElementById('output');

  // Check if BarcodeDetector is supported
  if (!('BarcodeDetector' in window)) {
    alert('Barcode detection is not supported by your browser.');
    console.error('Barcode Detector is not supported by this browser.');
    return;
  }

  scannerContainer.style.display = 'flex';

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    video.srcObject = stream;
    video.play();

    const barcodeDetector = new BarcodeDetector({
      formats: ['qr_code', 'code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e']
    });

    const scanFrame = () => {
      barcodeDetector.detect(video)
        .then(barcodes => {
          if (barcodes.length > 0) {
            barcodes.forEach(barcode => {
              console.log('Detected barcode:', barcode.rawValue);
              output.textContent = `Detected: ${barcode.rawValue}`;
            });
          } else {
            output.textContent = '';
          }
        })
        .catch(err => {
          console.error('Barcode detection failed:', err);
        });

      requestAnimationFrame(scanFrame);
    };

    video.addEventListener('playing', scanFrame);

  } catch (err) {
    console.error('Error accessing camera:', err);
    alert('Could not access the camera. Please allow camera access and try again.');
    scannerContainer.style.display = 'none';
  }
});

async function checkOverallStatus() {
  try {
    const operations = document.getElementById('operations-table')?.querySelectorAll('.op-table-data-row');
    if (!operations) return true;

    const disallowedStatuses = ["On Work Schedule", "In Progress", "Hold", "Needs Scheduling"];

    for (const operation of operations) {
      const data = JSON.parse(operation.getAttribute('data-jobinfo'));

      if (!data) continue;

      if (disallowedStatuses.includes(data.Status)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
}

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin) return;
  const data = event.data || {};
  if (data.action !== 'openTechSelect' || !data.job) return;

  // Build a hidden surrogate node that matches what populateTechSelectModal expects
  const dummy = document.createElement('div');
  dummy.className = 'job-item';
  dummy.style.display = 'none';
  dummy.setAttribute('data-jobinfo', JSON.stringify(data.job));
  document.body.appendChild(dummy);

  // Reuse your existing modal opener
  // populateTechSelectModal will add 'current-tech-select' to e.target,
  // set currentTechSelectJobId, build the tech list, and call .classList.add('active') on #techSelectModal
  populateTechSelectModal({ target: dummy }, 'maps');
});

function checkNoteCount(){
  const noteArea = document.getElementById('note-area');
  const noteCount = noteArea.querySelectorAll('.note-pair').length;

  if (noteCount === 0) {
    document.querySelector('.note-active img')?.classList.remove('has-notes');
    if (!noteArea.querySelector('.note-empty-state')) {
      noteArea.innerHTML = '<div class="note-empty-state">No notes yet</div>';
    }
  }
}

